import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { acquireLock, releaseLock, wasRecentlyLoaded, markAsLoaded } from '../utils/apiLock';
import { useSession } from '../context/SessionContext';
import { trackEvent, trackPageView } from '../services/analytics';
import { verifyAndGetCachedStory, getVerifiedTimelineCache, setVerifiedTimelineCache, clearAllTimelineCaches } from '../utils/verifyUserData';
import { rlog } from '../utils/railwayLogger';

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'https://taraanaya-prod-production.up.railway.app';

// Domain configuration
// Domain configurations - NO EMOJIS for professional output
const DOMAINS = [
  { key: 'health', name: 'Health', color: '#ef4444' },
  { key: 'career', name: 'Career', color: '#3b82f6' },
  { key: 'education', name: 'Education', color: '#6366f1' },
  { key: 'wealth', name: 'Wealth', color: '#22c55e' },
  { key: 'spiritual', name: 'Spirituality', color: '#14b8a6' },
  { key: 'travel', name: 'Travel', color: '#8b5cf6' },
  { key: 'family', name: 'Family', color: '#f59e0b' },
  { key: 'marriage', name: 'Marriage', color: '#ec4899' },
];

// Helper to load initial domain data SYNCHRONOUSLY from localStorage — WITH user verification
const getInitialTimelineData = () => {
  const domains = ['health', 'career', 'wealth', 'marriage', 'education', 'spiritual', 'travel', 'family'];
  const cachedResults = {};
  const currentUserId = localStorage.getItem('user_id');
  
  // PRIORITY 1: Check combined cache (user-stamped)
  const combined = getVerifiedTimelineCache('timeline_data_cache');
  if (combined && Object.keys(combined).length >= 1) {
    // Remove internal stamp before returning
    const { _userId, ...data } = combined;
    if (Object.keys(data).length >= 1) {
      console.log('EventTimeline: Loaded from verified combined cache (instant)');
      return data;
    }
  }
  
  // PRIORITY 2: Check individual domain caches (user-stamped)
  let loadedCount = 0;
  for (const domain of domains) {
    const parsed = getVerifiedTimelineCache(`${domain}_timeline_data`);
    if (parsed && Object.keys(parsed).length > 0) {
      const { _userId, ...data } = parsed;
      cachedResults[domain] = data;
      loadedCount++;
    }
  }
  
  if (loadedCount >= 1) {
    console.log(`EventTimeline: Loaded ${loadedCount}/8 domains from verified cache (instant)`);
    return cachedResults;
  }
  
  // PRIORITY 3: Extract from unified_story_data (INSTANT - no API call)
  try {
    const unifiedData = verifyAndGetCachedStory();
    if (unifiedData) {
      const allMahadashas = unifiedData.all_mahadashas || [];
      const currentMd = allMahadashas.find(m => m.status === 'current' || m.is_current) || allMahadashas[0];
      
      if (currentMd?.domain_predictions) {
        for (const domain of domains) {
          const domainPred = currentMd.domain_predictions[domain];
          if (domainPred) {
            cachedResults[domain] = {
              all_mahadashas: allMahadashas,
              insights: domainPred,
              monthly: {
                summary: domainPred.summary || `${domain} predictions`,
                advice: domainPred.advice || `Focus on ${domain}`,
                focus_areas: domainPred.focus_areas || []
              },
              daily: {
                advice: domainPred.advice || `Focus on ${domain} today`,
                level: domainPred.vitality?.overall || 'moderate',
                focus_areas: domainPred.focus_areas || []
              },
              today: {
                advice: domainPred.advice || `Focus on ${domain} today`,
                level: domainPred.vitality?.overall || 'moderate'
              },
              yearly: {
                events: [],
                themes: domainPred.themes || []
              }
            };
          }
        }
        
        const foundCount = Object.keys(cachedResults).length;
        if (foundCount >= 1) {
          console.log(`EventTimeline: Extracted ${foundCount}/8 domains from unified_story_data (instant)`);
          // Cache for faster future access — stamped with user_id
          setVerifiedTimelineCache('timeline_data_cache', cachedResults);
          return cachedResults;
        }
      }
    }
  } catch (e) {
    console.log('EventTimeline: Error extracting from unified_story_data:', e.message);
  }
  
  return {};
};

const EventTimeline = () => {
  // Only use loading state from SessionContext - birth details come from localStorage
  const { isLoading: sessionLoading } = useSession();
  
  const [view, setView] = useState('monthly');
  const [godLevelMode, setGodLevelMode] = useState(false); // NEW: God-Level mode toggle
  const [godLevelData, setGodLevelData] = useState(null); // NEW: God-Level weekly events
  const [godLevelDasha, setGodLevelDasha] = useState(null); // NEW: God-Level 5-level dasha
  const [godLevelLoading, setGodLevelLoading] = useState(false);
  const [godLevelWeekStart, setGodLevelWeekStart] = useState(new Date());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedDay, setSelectedDay] = useState(null);
  
  // Initialize with cached data SYNCHRONOUSLY - show UI immediately with any cached data
  const [allDomainsData, setAllDomainsData] = useState(() => getInitialTimelineData());
  const [dataLoaded, setDataLoaded] = useState(() => Object.keys(getInitialTimelineData()).length >= 1);
  const [loading, setLoading] = useState(() => Object.keys(getInitialTimelineData()).length < 1);
  const [loadingDomains, setLoadingDomains] = useState({}); // Track individual domain loading
  
  useEffect(() => {
    trackPageView('timeline');
    trackEvent('timeline_viewed', { is_premium: localStorage.getItem('isPremium') === 'true' });
    rlog('timeline_mount', {
      is_premium: localStorage.getItem('isPremium') === 'true',
      plan_id: localStorage.getItem('plan_id'),
      plan_unlocks: localStorage.getItem('plan_unlocks'),
      timeline_monthly_version: (() => { try { return JSON.parse(localStorage.getItem('content_versions') || '{}').timeline_monthly; } catch { return null; } })(),
      timeline_yearly_version: (() => { try { return JSON.parse(localStorage.getItem('content_versions') || '{}').timeline_yearly; } catch { return null; } })(),
    });
  }, []);

  // Detect user switch — reset all timeline state if user_id changed since last render
  const lastUserIdRef = useRef(localStorage.getItem('user_id'));
  useEffect(() => {
    const checkUserSwitch = () => {
      const currentUserId = localStorage.getItem('user_id');
      if (lastUserIdRef.current && currentUserId && lastUserIdRef.current !== currentUserId) {
        console.log(`User switched from ${lastUserIdRef.current} to ${currentUserId}. Resetting timeline state.`);
        clearAllTimelineCaches();
        setAllDomainsData({});
        setDataLoaded(false);
        setLoading(true);
        setRealTimelineData(null);
        setDashaHierarchy(null);
        setMonthlyPredictions(null);
        setYearlyOverviewData(null);
        setDomainInsights(null);
        setDetailedYearlyReport(null);
        setDetailedMonthlyData(null);
        setGodLevelData(null);
        setGodLevelDasha(null);
        setYearlyEventsData(null);
        lastUserIdRef.current = currentUserId;
      } else if (!lastUserIdRef.current && currentUserId) {
        lastUserIdRef.current = currentUserId;
      }
    };
    // Check on mount and periodically (covers navigation-based user switches)
    checkUserSwitch();
    const interval = setInterval(checkUserSwitch, 2000);
    return () => clearInterval(interval);
  }, []);
  // NEW: Real timeline data from API - try to load from localStorage first (VERIFIED)
  const [realTimelineData, setRealTimelineData] = useState(() => {
    const cached = getVerifiedTimelineCache('real_timeline_data');
    if (cached) {
      const now = new Date();
      if (cached.data && cached.month === now.getMonth() && cached.year === now.getFullYear()) {
        console.log('Loaded real_timeline_data from verified cache');
        return cached.data;
      }
    }
    return null;
  });
  const [dashaHierarchy, setDashaHierarchy] = useState(() => {
    const cached = getVerifiedTimelineCache('real_timeline_data');
    if (cached?.data?.current_hierarchy) {
      return cached.data.current_hierarchy;
    }
    return null;
  });
  const [monthlyPredictions, setMonthlyPredictions] = useState(() => {
    const cached = getVerifiedTimelineCache('real_timeline_data');
    if (cached?.data?.monthly_predictions) {
      return cached.data.monthly_predictions;
    }
    return null;
  });
  const [yearlyOverviewData, setYearlyOverviewData] = useState(() => {
    const cached = getVerifiedTimelineCache('real_timeline_data');
    if (cached?.data?.yearly_overview) {
      return cached.data.yearly_overview;
    }
    return null;
  });
  const [domainInsights, setDomainInsights] = useState(() => {
    // Try real_timeline_data first (verified)
    const cached = getVerifiedTimelineCache('real_timeline_data');
    if (cached?.data?.domain_insights) {
      console.log('Loaded domain_insights from verified real_timeline_data cache');
      return cached.data.domain_insights;
    }
    // Fallback to domain_insights_cache (verified)
    const diCache = getVerifiedTimelineCache('domain_insights_cache');
    if (diCache) {
      const { _userId, ...data } = diCache;
      console.log('Loaded domain_insights from verified domain_insights_cache');
      return data;
    }
    // Fallback to unified_story_data (already user-verified)
    const unified = verifyAndGetCachedStory();
    if (unified?.domain_insights) {
      console.log('Loaded domain_insights from verified unified_story_data');
      return unified.domain_insights;
    }
    return null;
  });
  
  // NEW: Detailed LLM yearly report - try to load from localStorage first
  // VERSION CHECK: Clear old cache if version mismatch
  const TIMELINE_VERSION = 'v4_daily_deepdive';
  
  // NEW: Comprehensive yearly events (100s of events)
  const [yearlyEventsData, setYearlyEventsData] = useState(null);
  const [loadingYearlyEvents, setLoadingYearlyEvents] = useState(false);
  
  const [detailedYearlyReport, setDetailedYearlyReport] = useState(() => {
    const cached = getVerifiedTimelineCache('detailed_yearly_report');
    if (cached?.data && cached.year === new Date().getFullYear()) {
      console.log('Loaded detailed yearly report from verified cache');
      return cached.data;
    }
    // Fallback to unified_story_data (already user-verified)
    const unified = verifyAndGetCachedStory();
    if (unified?.yearly_predictions) {
      console.log('Loaded yearly predictions from verified unified_story_data');
      return unified.yearly_predictions;
    }
    return null;
  });
  const [loadingYearlyReport, setLoadingYearlyReport] = useState(false);

  // NEW: Detailed monthly - try to load from verified localStorage first
  const [detailedMonthlyData, setDetailedMonthlyData] = useState(() => {
    const cached = getVerifiedTimelineCache('detailed_monthly_data');
    if (cached) {
      // Check version - if old version or missing daily_details, clear it
      if (cached.data?.prediction_version !== TIMELINE_VERSION || !cached.data?.daily_details) {
        console.log('Old monthly cache version detected, clearing...');
        localStorage.removeItem('detailed_monthly_data');
        return null;
      }
      const now = new Date();
      if (cached.data?.daily_predictions && 
          cached.month === now.getMonth() && 
          cached.year === now.getFullYear()) {
        console.log('Loaded detailed monthly data from verified cache');
        return cached.data;
      }
    }
    return null;
  });
  const [loadingMonthlyDetailed, setLoadingMonthlyDetailed] = useState(false);
  const retryAttemptedRef = useRef(false);

  const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                  'July', 'August', 'September', 'October', 'November', 'December'];
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const getUserId = () => {
    return localStorage.getItem('birth_session_id') 
      || localStorage.getItem('session_id')
      || localStorage.getItem('user_id')
      || null;
  };

  // Load birth details from localStorage first (instant), then MongoDB
  const loadBirthDetails = async () => {
    // STEP 1: Try ALL localStorage keys FIRST (instant load) - USER'S REAL DATA
    const keysToTry = ['birthDetails', 'birth_details', 'birthData', 'completeBirthData'];
    
    for (const key of keysToTry) {
      const stored = localStorage.getItem(key);
      if (stored) {
        try {
          const data = JSON.parse(stored);
          
          // Try to extract year/month/day - support both direct fields and dateOfBirth string
          let year, month, day;
          
          // First try direct fields
          year = data.year || data.birthYear;
          month = data.month || data.birthMonth;
          day = data.day || data.birthDay;
          
          // If not found, try parsing dateOfBirth string (format: "1997-05-14" or "14/05/1997")
          if (!year && data.dateOfBirth) {
            const dateParts = data.dateOfBirth.split(/[-\/]/);
            if (dateParts.length === 3) {
              if (dateParts[0].length === 4) {
                // YYYY-MM-DD format
                year = parseInt(dateParts[0]);
                month = parseInt(dateParts[1]);
                day = parseInt(dateParts[2]);
              } else {
                // DD/MM/YYYY format
                day = parseInt(dateParts[0]);
                month = parseInt(dateParts[1]);
                year = parseInt(dateParts[2]);
              }
            }
          }
          
          // Try to extract hour/minute - support both direct fields and timeOfBirth string
          let hour = data.hour || data.birthHour || 12;
          let minute = data.minute || data.birthMinute || 0;
          
          if (data.timeOfBirth) {
            const timeMatch = data.timeOfBirth.match(/(\d+):(\d+)\s*(AM|PM)?/i);
            if (timeMatch) {
              hour = parseInt(timeMatch[1]);
              minute = parseInt(timeMatch[2]);
              const period = timeMatch[3]?.toUpperCase();
              if (period === 'PM' && hour !== 12) hour += 12;
              if (period === 'AM' && hour === 12) hour = 0;
            }
          }
          
          const hasLat = data.latitude || data.lat;
          
          if (year && month && day && hasLat) {
            // Normalize the data structure
            const parsed = {
              name: data.name || data.userName || localStorage.getItem('userName') || 'User',
              year: year,
              month: month,
              day: day,
              hour: hour,
              minute: minute,
              latitude: data.latitude || data.lat,
              longitude: data.longitude || data.lon || 77.209,
              gender: data.gender || 'unknown'
            };
            console.log(`✅ EventTimeline: Birth details from localStorage key "${key}":`, parsed.name, parsed.year, parsed.month, parsed.day);
            // Also save to normalized key for future quick access
            localStorage.setItem('birth_details', JSON.stringify(parsed));
            return parsed;
          }
        } catch (e) {
          console.log(`⚠️ EventTimeline: localStorage parse error for ${key}:`, e.message);
        }
      }
    }
    
    // STEP 2: Try MongoDB via unified-story load
    const sessionId = getUserId();
    if (sessionId) {
      try {
        console.log('🔍 EventTimeline: Loading from MongoDB via unified-story...');
        const response = await fetch(`${API_BASE}/api/unified-story/load/${sessionId}`);
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data?.user_info) {
            const userInfo = result.data.user_info;
            const birthData = {
              name: userInfo.name,
              year: userInfo.year,
              month: userInfo.month,
              day: userInfo.day,
              hour: userInfo.hour,
              minute: userInfo.minute,
              latitude: userInfo.latitude,
              longitude: userInfo.longitude,
              gender: userInfo.gender || 'unknown'
            };
            localStorage.setItem('birth_details', JSON.stringify(birthData));
            localStorage.setItem('birthDetails', JSON.stringify(birthData));
            console.log('✅ EventTimeline: Birth details loaded from MongoDB:', birthData.name);
            return birthData;
          }
        }
      } catch (e) {
        console.log('⚠️ EventTimeline: MongoDB unified-story load failed:', e.message);
      }
    }
    
    // STEP 2.5: Try direct birth_details endpoint by device_id
    const deviceId = localStorage.getItem('device_id') || localStorage.getItem('taraanaya_device_id');
    if (deviceId) {
      try {
        console.log('🔍 EventTimeline: Loading birth details by device_id...');
        const response = await fetch(`${API_BASE}/api/auth/birth/by-device/${deviceId}`);
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            const bd = result.data;
            const birthData = {
              name: bd.name || 'User',
              year: bd.year,
              month: bd.month,
              day: bd.day,
              hour: bd.hour || 12,
              minute: bd.minute || 0,
              latitude: bd.latitude,
              longitude: bd.longitude,
              gender: bd.gender || 'unknown'
            };
            localStorage.setItem('birth_details', JSON.stringify(birthData));
            localStorage.setItem('birthDetails', JSON.stringify(birthData));
            console.log('✅ EventTimeline: Birth details loaded by device_id:', birthData.name);
            return birthData;
          }
        }
      } catch (e) {
        console.log('⚠️ EventTimeline: Device birth load failed:', e.message);
      }
    }
    
    // STEP 3: Reconstruct from individual keys
    const birthDate = localStorage.getItem('birthDate');
    const birthCoordinates = localStorage.getItem('birthCoordinates');
    if (birthDate && birthCoordinates) {
      try {
        const date = JSON.parse(birthDate);
        const time = JSON.parse(localStorage.getItem('birthTime') || '{}');
        const coords = JSON.parse(birthCoordinates);
        let hour = time.hour || 12;
        if (time.period === 'PM' && hour !== 12) hour += 12;
        if (time.period === 'AM' && hour === 12) hour = 0;
        
        const reconstructed = {
          name: localStorage.getItem('userName') || 'User',
          year: date.year,
          month: date.month,
          day: date.day,
          hour: hour,
          minute: time.minute || 0,
          latitude: coords.latitude || coords.lat,
          longitude: coords.longitude || coords.lon,
          gender: localStorage.getItem('gender') || 'unknown'
        };
        localStorage.setItem('birthDetails', JSON.stringify(reconstructed));
        return reconstructed;
      } catch (e) {
        // Ignore parse errors for reconstructed birth data
      }
    }
    
    return null;
  };

  // NEW: Fetch REAL timeline predictions from backend API
  const fetchRealTimelinePredictions = useCallback(async (birthDetails, targetMonth, targetYear) => {
    if (!birthDetails) return;
    
    // Validate required fields
    const year = birthDetails.year || birthDetails.birthYear;
    const month = birthDetails.month || birthDetails.birthMonth;
    const day = birthDetails.day || birthDetails.birthDay;
    const latitude = birthDetails.latitude || birthDetails.lat;
    
    if (!year || !month || !day || !latitude) {
      console.error('❌ EventTimeline: Invalid birth details for comprehensive - missing required fields:', { year, month, day, latitude });
      return;
    }
    
    try {
      console.log('📡 EventTimeline: Fetching REAL predictions from API...');
      const tlStart = Date.now();
      
      // Get user_id for canonical birth data lookup
      const userId = localStorage.getItem('birth_session_id') 
        || localStorage.getItem('session_id')
        || localStorage.getItem('user_id')
        || null;
      
      rlog('timeline_comprehensive_start', {
        user_id: userId,
        target_year: targetYear,
        target_month: targetMonth + 1,
        content_version: (() => { try { return JSON.parse(localStorage.getItem('content_versions') || '{}').timeline_monthly; } catch { return null; } })(),
      });
      
      const response = await fetch(`${API_BASE}/api/timeline/comprehensive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          person_id: userId || 'default',
          year: year,
          month: month,
          day: day,
          hour: birthDetails.hour || birthDetails.birthHour || 12,
          minute: birthDetails.minute || birthDetails.birthMinute || 0,
          latitude: latitude,
          longitude: birthDetails.longitude || birthDetails.lon || 77.209,
          target_month: targetMonth + 1, // API uses 1-indexed months
          target_year: targetYear,
          user_id: userId  // CRITICAL: Send user_id for canonical birth data lookup
        })
      });
      
      if (!response.ok) {
        // Fallback to timeline/generate endpoint if comprehensive doesn't exist
        console.log('📡 Trying fallback endpoint...');
        rlog('timeline_comprehensive_fallback', { user_id: userId, http_status: response.status });
        const fallbackResponse = await fetch(`${API_BASE}/api/timeline/generate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            person_id: userId || 'default',
            birth_data: {
              year, month, day,
              hour: birthDetails.hour || 12,
              minute: birthDetails.minute || 0,
              latitude,
              longitude: birthDetails.longitude || 77.209
            }
          })
        });
        
        if (fallbackResponse.ok) {
          const fallbackResult = await fallbackResponse.json();
          console.log('✅ EventTimeline: Fallback predictions loaded');
          rlog('timeline_fallback_ok', { user_id: userId, elapsed_ms: Date.now() - tlStart });
          // Transform fallback response to expected format
          if (fallbackResult) {
            setRealTimelineData(fallbackResult);
            return;
          }
        }
        return;
      }
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          console.log('✅ EventTimeline: REAL predictions loaded successfully');
          rlog('timeline_comprehensive_done', {
            user_id: userId,
            elapsed_ms: Date.now() - tlStart,
            http_status: response.status,
            algorithm_version: result.data?.algorithm_version || result.algorithm_version,
            has_monthly: !!result.data.monthly_predictions,
            has_yearly: !!result.data.yearly_overview,
          });
          setRealTimelineData(result.data);
          setDashaHierarchy(result.data.current_hierarchy);
          setMonthlyPredictions(result.data.monthly_predictions);
          setYearlyOverviewData(result.data.yearly_overview);
          setDomainInsights(result.data.domain_insights);
          
          // Cache for future use (user-stamped)
          setVerifiedTimelineCache('real_timeline_data', {
            data: result.data,
            month: targetMonth,
            year: targetYear,
            timestamp: Date.now()
          });
          
          return result.data;
        }
      } else {
        console.warn('⚠️ EventTimeline: API returned non-OK status');
      }
    } catch (e) {
      console.error('❌ EventTimeline: Error fetching real predictions:', e.message);
    }
    
    return null;
  }, []);

  // NEW: Fetch detailed LLM-powered yearly report
  const fetchDetailedYearlyReport = useCallback(async (birthDetails, targetYear) => {
    if (!birthDetails || loadingYearlyReport) return;
    
    // Validate required fields
    const year = birthDetails.year || birthDetails.birthYear;
    const month = birthDetails.month || birthDetails.birthMonth;
    const day = birthDetails.day || birthDetails.birthDay;
    const latitude = birthDetails.latitude || birthDetails.lat;
    
    if (!year || !month || !day || !latitude) {
      console.error('❌ EventTimeline: Invalid birth details - missing required fields:', { year, month, day, latitude });
      return;
    }
    
    setLoadingYearlyReport(true);
    try {
      console.log('📡 EventTimeline: Fetching detailed yearly report from LLM...', birthDetails.name, targetYear);
      
      // Get user_id for canonical birth data lookup
      const userId = localStorage.getItem('birth_session_id') 
        || localStorage.getItem('session_id')
        || localStorage.getItem('user_id')
        || null;
      
      const response = await fetch(`${API_BASE}/api/timeline/yearly-detailed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          person_id: userId || 'default',
          year: year,
          month: month,
          day: day,
          hour: birthDetails.hour || birthDetails.birthHour || 12,
          minute: birthDetails.minute || birthDetails.birthMinute || 0,
          latitude: latitude,
          longitude: birthDetails.longitude || birthDetails.lon || 77.209,
          name: birthDetails.name || 'User',
          target_year: targetYear,
          user_id: userId  // CRITICAL: Send user_id for canonical birth data lookup
        })
      });
      
      if (!response.ok) {
        // Fallback to varsaphala endpoint if yearly-detailed doesn't exist
        console.log('📡 Trying varsaphala fallback for yearly...');
        const fallbackResponse = await fetch(`${API_BASE}/api/varsaphala/single-year`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            year: year,
            month: month,
            day: day,
            hour: birthDetails.hour || 12,
            minute: birthDetails.minute || 0,
            latitude: latitude,
            longitude: birthDetails.longitude || 77.209,
            target_year: targetYear
          })
        });
        
        if (fallbackResponse.ok) {
          const fallbackResult = await fallbackResponse.json();
          console.log('✅ Varsaphala yearly loaded as fallback');
          setDetailedYearlyReport(fallbackResult);
          return fallbackResult;
        }
        return null;
      }
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          console.log('✅ EventTimeline: Detailed yearly report loaded!');
          setDetailedYearlyReport(result.data);
          
          // Cache for future use (user-stamped)
          setVerifiedTimelineCache('detailed_yearly_report', {
            data: result.data,
            year: targetYear,
            timestamp: Date.now()
          });
          
          return result.data;
        }
      }
    } catch (e) {
      console.error('❌ EventTimeline: Error fetching detailed yearly report:', e.message);
    } finally {
      setLoadingYearlyReport(false);
    }
    
    return null;
  }, [loadingYearlyReport]);

  // NEW: Fetch comprehensive yearly events (100s of events)
  const fetchYearlyEvents = useCallback(async (birthDetails, targetYear) => {
    if (!birthDetails || loadingYearlyEvents) return;
    
    const year = birthDetails.year || birthDetails.birthYear;
    const month = birthDetails.month || birthDetails.birthMonth;
    const day = birthDetails.day || birthDetails.birthDay;
    const latitude = birthDetails.latitude || birthDetails.lat;
    
    if (!year || !month || !day || !latitude) return;
    
    setLoadingYearlyEvents(true);
    try {
      console.log('📡 EventTimeline: Fetching comprehensive yearly events...', targetYear);
      
      const userId = localStorage.getItem('birth_session_id') 
        || localStorage.getItem('session_id')
        || localStorage.getItem('user_id')
        || null;
      
      const response = await fetch(`${API_BASE}/api/timeline/yearly-events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          year: year,
          month: month,
          day: day,
          hour: birthDetails.hour || 12,
          minute: birthDetails.minute || 0,
          latitude: latitude,
          longitude: birthDetails.longitude || birthDetails.lon || 77.209,
          name: birthDetails.name || 'User',
          target_year: targetYear,
          user_id: userId
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          console.log(`✅ Loaded ${result.data.total_events} yearly events!`);
          setYearlyEventsData(result.data);
          return result.data;
        }
      }
    } catch (e) {
      console.error('❌ Error fetching yearly events:', e.message);
    } finally {
      setLoadingYearlyEvents(false);
    }
    return null;
  }, [loadingYearlyEvents]);

  // Fetch detailed monthly predictions for daily events
  const fetchDetailedMonthlyPredictions = useCallback(async (birthDetails, targetMonth, targetYear) => {
    if (!birthDetails || loadingMonthlyDetailed) return;
    
    // Validate required fields
    const year = birthDetails.year || birthDetails.birthYear;
    const month = birthDetails.month || birthDetails.birthMonth;
    const day = birthDetails.day || birthDetails.birthDay;
    const latitude = birthDetails.latitude || birthDetails.lat;
    
    if (!year || !month || !day || !latitude) {
      console.error('❌ EventTimeline: Invalid birth details for monthly - missing required fields:', { year, month, day, latitude });
      return;
    }
    
    setLoadingMonthlyDetailed(true);
    try {
      console.log('📡 Fetching detailed monthly predictions...', birthDetails.name, targetMonth + 1, targetYear);
      
      // Get user_id for canonical birth data lookup
      const userId = localStorage.getItem('birth_session_id') 
        || localStorage.getItem('session_id')
        || localStorage.getItem('user_id')
        || null;
      
      const response = await fetch(`${API_BASE}/api/timeline/monthly-detailed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          person_id: userId || 'default',
          year: year,
          month: month,
          day: day,
          hour: birthDetails.hour || birthDetails.birthHour || 12,
          minute: birthDetails.minute || birthDetails.birthMinute || 0,
          latitude: latitude,
          longitude: birthDetails.longitude || birthDetails.lon || 77.209,
          name: birthDetails.name || 'User',
          target_month: targetMonth + 1,
          target_year: targetYear,
          user_id: userId  // CRITICAL: Send user_id for canonical birth data lookup
        })
      });
      
      if (!response.ok) {
        // Fallback to health timeline endpoint if monthly-detailed doesn't exist
        console.log('📡 Trying health timeline fallback for monthly...');
        const fallbackResponse = await fetch(`${API_BASE}/api/health/timeline/monthly-detailed/${targetYear}/${targetMonth + 1}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            year: year,
            month: month,
            day: day,
            hour: birthDetails.hour || 12,
            minute: birthDetails.minute || 0,
            latitude: latitude,
            longitude: birthDetails.longitude || 77.209
          })
        });
        
        if (fallbackResponse.ok) {
          const fallbackResult = await fallbackResponse.json();
          console.log('✅ Health timeline monthly loaded as fallback');
          setDetailedMonthlyData(fallbackResult);
          return fallbackResult;
        }
        return null;
      }
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          console.log('✅ Detailed monthly predictions loaded');
          setDetailedMonthlyData(result.data);
          
          // Cache for future use (user-stamped)
          setVerifiedTimelineCache('detailed_monthly_data', {
            data: result.data,
            month: targetMonth,
            year: targetYear,
            timestamp: Date.now()
          });
          
          return result.data;
        }
      }
    } catch (e) {
      console.error('Error fetching monthly detailed:', e.message);
    } finally {
      setLoadingMonthlyDetailed(false);
    }
    return null;
  }, [loadingMonthlyDetailed]);

  // Refresh predictions - clears cache and refetches all data
  const refreshPredictions = useCallback(async () => {
    console.log('🔄 Refreshing all predictions...');
    
    // Clear all state
    setDashaHierarchy(null);
    setDetailedMonthlyData(null);
    setDetailedYearlyReport(null);
    setRealTimelineData(null);
    setMonthlyPredictions(null);
    setYearlyOverviewData(null);
    setDomainInsights(null);
    
    // Clear localStorage caches (all timeline)
    clearAllTimelineCaches();
    
    // Get user ID for cache clearing on backend
    const userId = getUserId();
    if (userId) {
      try {
        await fetch(`${API_BASE}/api/timeline/cache/clear`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: userId })
        });
        console.log('✅ Backend cache cleared');
      } catch (e) {
        console.log('⚠️ Could not clear backend cache:', e.message);
      }
    }
    
    // Reload birth details and fetch fresh data
    const birthDetails = await loadBirthDetails();
    if (birthDetails) {
      console.log('📡 Fetching fresh predictions for:', birthDetails.name);
      
      // First, clear server-side cache to get fresh computed data
      try {
        const userId = getUserId();
        await fetch(`${API_BASE}/api/timeline/cache/clear`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            user_id: userId,
            clear_type: 'all'
          })
        });
        console.log('✅ Server cache cleared');
      } catch (e) {
        console.log('⚠️ Could not clear server cache:', e.message);
      }
      
      // Fetch all data in parallel
      await Promise.all([
        fetchRealTimelinePredictions(birthDetails, selectedMonth, selectedYear),
        fetchDetailedMonthlyPredictions(birthDetails, selectedMonth, selectedYear),
        fetchDetailedYearlyReport(birthDetails, selectedYear)
      ]);
      
      console.log('✅ All predictions refreshed!');
    } else {
      console.log('⚠️ No birth details found for refresh');
    }
  }, [selectedMonth, selectedYear, fetchRealTimelinePredictions, fetchDetailedMonthlyPredictions, fetchDetailedYearlyReport]);

  // NEW: Fetch God-Level predictions (5-level dasha + transits)
  const fetchGodLevelPredictions = useCallback(async (startDate = new Date()) => {
    setGodLevelLoading(true);
    
    try {
      // Load birth details
      const keysToTry = ['birthDetails', 'birth_details', 'birthData', 'completeBirthData'];
      let birthDetails = null;
      
      for (const key of keysToTry) {
        const stored = localStorage.getItem(key);
        if (stored) {
          try {
            const data = JSON.parse(stored);
            let year = data.year || data.birthYear;
            let month = data.month || data.birthMonth;
            let day = data.day || data.birthDay;
            
            if (!year && data.dateOfBirth) {
              const parts = data.dateOfBirth.split(/[-\/]/);
              if (parts.length === 3) {
                if (parts[0].length === 4) { [year, month, day] = parts.map(p => parseInt(p)); }
                else { [day, month, year] = parts.map(p => parseInt(p)); }
              }
            }
            
            if (year && month && day) {
              birthDetails = {
                name: data.name || 'User', year, month, day,
                hour: data.hour || 12, minute: data.minute || 0,
                latitude: data.latitude || 28.6139, longitude: data.longitude || 77.209,
                gender: data.gender || 'unknown', timezone_offset: 5.5
              };
              break;
            }
          } catch (e) {}
        }
      }
      
      if (!birthDetails) {
        console.log('⚠️ No birth details for God-Level fetch');
        setGodLevelLoading(false);
        return;
      }
      
      // Fetch weekly god-level predictions
      const weeklyResp = await fetch(`${API_BASE}/api/timeline/god-level/weekly`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...birthDetails,
          target_date: startDate.toISOString().split('T')[0]
        })
      });
      
      if (weeklyResp.ok) {
        const weeklyResult = await weeklyResp.json();
        if (weeklyResult.success) {
          setGodLevelData(weeklyResult);
          console.log('✅ God-Level weekly predictions loaded:', weeklyResult.days_with_events, 'days with events');
        }
      }
      
      // Also fetch daily for dasha info
      const dailyResp = await fetch(`${API_BASE}/api/timeline/god-level/daily`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(birthDetails)
      });
      
      if (dailyResp.ok) {
        const dailyResult = await dailyResp.json();
        if (dailyResult.success && dailyResult.dasha_hierarchy) {
          setGodLevelDasha(dailyResult.dasha_hierarchy);
          console.log('✅ God-Level dasha loaded:', dailyResult.dasha_hierarchy.full_string);
        }
      }
    } catch (e) {
      console.error('❌ God-Level fetch error:', e);
    } finally {
      setGodLevelLoading(false);
    }
  }, []);

  // Effect to load God-Level data when mode is enabled
  useEffect(() => {
    if (godLevelMode && !godLevelData) {
      fetchGodLevelPredictions(godLevelWeekStart);
    }
  }, [godLevelMode, godLevelData, godLevelWeekStart, fetchGodLevelPredictions]);

  // Helper function to generate daily calendar from domain predictions
  const generateDailyCalendar = (domainPred, domain) => {
    const calendar = [];
    const currentDate = new Date();
    const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
    
    // Use V3 algorithm data to generate varied predictions
    const positiveThemes = domainPred.positive_themes || domainPred.strengths || [];
    const negativeThemes = domainPred.challenges || domainPred.risks || [];
    const advice = domainPred.advice || `Focus on ${domain.name.toLowerCase()}`;
    
    for (let day = 1; day <= daysInMonth; day++) {
      const dayOfWeek = new Date(currentDate.getFullYear(), currentDate.getMonth(), day).getDay();
      
      // Create varied daily advice based on day and domain predictions
      let level = 'normal';
      let dayAdvice = advice;
      
      // Use planetary day associations for variation
      const planetaryDays = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'];
      const planetDay = planetaryDays[dayOfWeek];
      
      // Favorable days based on domain
      const favorableDays = {
        health: [0, 4], // Sunday (Sun), Thursday (Jupiter)
        career: [2, 4], // Tuesday (Mars), Thursday (Jupiter)
        wealth: [3, 5], // Wednesday (Mercury), Friday (Venus)
        marriage: [1, 5], // Monday (Moon), Friday (Venus)
        family: [1, 4], // Monday (Moon), Thursday (Jupiter)
        education: [3, 4], // Wednesday (Mercury), Thursday (Jupiter)
        travel: [3, 5], // Wednesday (Mercury), Friday (Venus)
        spiritual: [0, 4, 6] // Sunday, Thursday, Saturday
      };
      
      const isFavorable = favorableDays[domain.key]?.includes(dayOfWeek);
      
      if (isFavorable) {
        level = 'excellent';
        dayAdvice = positiveThemes[day % Math.max(positiveThemes.length, 1)] || `Excellent day for ${domain.name.toLowerCase()} activities`;
      } else if (day % 7 === 0) {
        level = 'good';
        dayAdvice = `Good day to plan ${domain.name.toLowerCase()} matters`;
      } else if (day % 11 === 0 || day % 13 === 0) {
        level = 'caution';
        dayAdvice = negativeThemes[day % Math.max(negativeThemes.length, 1)] || `Be careful with ${domain.name.toLowerCase()} decisions`;
      }
      
      calendar.push({
        day,
        level,
        advice: dayAdvice,
        dayOfWeek,
        planetDay
      });
    }
    
    return calendar;
  };

  // Helper function to generate yearly events from domain predictions
  const generateYearlyEvents = (domainPred, domain) => {
    const events = [];
    
    // Generate quarterly events based on domain
    const quarters = ['Q1 (Jan-Mar)', 'Q2 (Apr-Jun)', 'Q3 (Jul-Sep)', 'Q4 (Oct-Dec)'];
    const themes = domainPred.themes || domainPred.focus_areas || [`${domain.name} growth`];
    
    quarters.forEach((quarter, idx) => {
      events.push({
        period: quarter,
        theme: themes[idx % themes.length] || `Focus on ${domain.name.toLowerCase()}`,
        level: idx % 2 === 0 ? 'positive' : 'neutral',
        advice: domainPred.advice || `Continue ${domain.name.toLowerCase()} activities`
      });
    });
    
    return events;
  };

  const loadAllDomainsFromCache = useCallback(async () => {
    // STEP 1: Check localStorage cache FIRST (instant load) — VERIFIED
    const cachedTimeline = getVerifiedTimelineCache('timeline_data_cache');
    if (cachedTimeline) {
      const { _userId, ...data } = cachedTimeline;
      if (Object.keys(data).length >= 4) {
        console.log('EventTimeline: Loaded from verified cache (instant)');
        setAllDomainsData(data);
        setDataLoaded(true);
        setLoading(false);
        return;
      }
    }
    
    setLoading(true);
    const domainData = {};
    
    // Get birth details (try localStorage first, then MongoDB)
    const birthDetails = await loadBirthDetails();
    
    if (!birthDetails) {
      console.log('EventTimeline: No birth details found');
      setLoading(false);
      return;
    }
    
    const userId = getUserId();
    
    // Try to load from verified individual domain caches first
    let loadedFromCache = 0;
    for (const domain of DOMAINS) {
      const cached = getVerifiedTimelineCache(`${domain.key}_timeline_data`);
      if (cached) {
        const { _userId, ...data } = cached;
        domainData[domain.key] = data;
        loadedFromCache++;
      }
    }
    
    if (loadedFromCache >= 4) {
      console.log(`✅ EventTimeline: Loaded ${loadedFromCache}/8 from localStorage cache (instant)`);
      setAllDomainsData(domainData);
      setDataLoaded(true);
      setVerifiedTimelineCache('timeline_data_cache', domainData);
      setLoading(false);
      return;
    }
    
    // Check if recently loaded by another component
    if (wasRecentlyLoaded('domains_all_parallel', 60000)) {
      console.log('⏭️ EventTimeline: Skipping API call - recently loaded');
      setLoading(false);
      return;
    }
    
    // Try to acquire lock
    if (!acquireLock('domains_all_parallel')) {
      console.log('⏭️ EventTimeline: Skipping API call - another component is loading');
      setLoading(false);
      return;
    }
    
    // USE FAST BATCH ENDPOINT instead of 8 individual calls
    console.log('📡 EventTimeline: Loading ALL domains via fast batch endpoint...');
    try {
      const response = await fetch(`${API_BASE}/api/domains/all-parallel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: birthDetails.name || 'User',
          year: birthDetails.year || birthDetails.birthYear,
          month: birthDetails.month || birthDetails.birthMonth,
          day: birthDetails.day || birthDetails.birthDay,
          hour: birthDetails.hour || 12,
          minute: birthDetails.minute || 0,
          latitude: birthDetails.latitude || birthDetails.lat,
          longitude: birthDetails.longitude || birthDetails.lon,
          gender: birthDetails.gender || 'unknown'
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          Object.entries(result.data).forEach(([domain, data]) => {
            domainData[domain] = data;
            setVerifiedTimelineCache(`${domain}_timeline_data`, data);
          });
          markAsLoaded('domains_all_parallel');
          console.log(`✅ EventTimeline: Loaded ${result.loaded_count}/8 domains in ${result.elapsed_seconds}s`);
        }
      }
    } catch (e) {
      console.log('⚠️ Batch endpoint error:', e.message);
    } finally {
      releaseLock('domains_all_parallel');
    }
    
    // SECOND: Try to load from unified_story as backup (contains domain_predictions)
    if (Object.keys(domainData).length < 4) {
      try {
        let unifiedData = verifyAndGetCachedStory();
        if (unifiedData) {
          console.log('EventTimeline: Loaded verified unified story from localStorage');
        }
        
        // If not in localStorage, try MongoDB
        if (!unifiedData && userId) {
          const response = await fetch(`${API_BASE}/api/unified-story/load/${userId}`);
          if (response.ok) {
            const result = await response.json();
            if (result.success && result.data) {
              unifiedData = result.data;
              console.log('✅ EventTimeline: Loaded unified story from MongoDB');
            }
          }
        }
        
        // Extract domain data from unified story
        if (unifiedData) {
          const futureMahadashas = unifiedData.future_story?.mahadashas || 
                                    unifiedData.all_mahadashas?.filter(m => m.status === 'current' || m.status === 'future') || [];
          
          DOMAINS.forEach(domain => {
            if (domainData[domain.key]) return; // Skip if already loaded
            
            const domainKey = domain.key;
            const currentMd = futureMahadashas.find(m => m.status === 'current') || futureMahadashas[0];
            const domainPred = currentMd?.domain_predictions?.[domainKey] || {};
            
            domainData[domainKey] = {
              all_mahadashas: futureMahadashas,
              insights: domainPred,
              monthly: {
                summary: domainPred.summary || `${domain.name} predictions for this month`,
                advice: domainPred.advice || `Focus on ${domain.name.toLowerCase()}`,
                focus_areas: domainPred.focus_areas || [],
                daily_calendar: generateDailyCalendar(domainPred, domain)
              },
              daily: {
                advice: domainPred.advice || `Focus on ${domain.name.toLowerCase()} today`,
                level: domainPred.vitality?.overall || 'moderate',
                focus_areas: domainPred.focus_areas || []
              },
              today: {
                advice: domainPred.advice || `Focus on ${domain.name.toLowerCase()} today`,
                level: domainPred.vitality?.overall || 'moderate'
              },
              yearly: {
                events: generateYearlyEvents(domainPred, domain),
                themes: domainPred.themes || []
              }
            };
          });
          console.log('✅ EventTimeline: Extracted domains from unified story');
        }
      } catch (e) {
        console.error('Error loading unified story for EventTimeline:', e);
      }
    }
    
    // THIRD: Load from individual domain load endpoints and localStorage as final fallback
    const endpoints = DOMAINS.map(d => ({
      key: d.key,
      url: `/api/${d.key}-timeline/load/${userId}`,
      storage: `${d.key}_timeline_data`
    }));
    
    await Promise.all(
      endpoints.map(async ({ key, url, storage }) => {
        // Skip if already loaded
        if (domainData[key]?.daily) return;
        
        try {
          // Try verified localStorage first
          const cached = getVerifiedTimelineCache(storage);
          if (cached) {
            const { _userId, ...data } = cached;
            domainData[key] = data;
            return;
          }
          
          // Then try MongoDB
          if (userId) {
            const res = await fetch(`${API_BASE}${url}`);
            if (res.ok) {
              const result = await res.json();
              if (result.success && result.data) {
                domainData[key] = result.data;
                return;
              }
            }
          }
        } catch (e) {
          console.log(`Error loading ${key}:`, e.message);
        }
      })
    );
    
    setAllDomainsData(domainData);
    setDataLoaded(true);
    setLoading(false);
    console.log('✅ EventTimeline: All domains loaded', Object.keys(domainData));
  }, [generateDailyCalendar, generateYearlyEvents]);

  useEffect(() => {
    // If we already have data from initial state, don't need to load
    const initialDataCount = Object.keys(getInitialTimelineData()).length;
    if (initialDataCount >= 4) {
      console.log('✅ EventTimeline: Using initial cached data, skipping API');
    } else {
      loadAllDomainsFromCache();
    }
    
    // Only fetch REAL predictions if not already cached
    if (!realTimelineData) {
      const loadRealPredictions = async () => {
        const birthDetails = await loadBirthDetails();
        if (birthDetails) {
          await fetchRealTimelinePredictions(birthDetails, selectedMonth, selectedYear);
        }
      };
      loadRealPredictions();
    }
  }, [loadAllDomainsFromCache, fetchRealTimelinePredictions, selectedMonth, selectedYear]);

  // Fetch detailed yearly report when switching to yearly view
  useEffect(() => {
    // Always try to fetch if we don't have LLM-quality data
    const needsFetch = !detailedYearlyReport || !detailedYearlyReport.full_report;
    const needsEvents = !yearlyEventsData || yearlyEventsData.year !== selectedYear;
    
    if (view === 'yearly' && (needsFetch || needsEvents) && !loadingYearlyReport && !loadingYearlyEvents) {
      const loadYearlyData = async () => {
        const birthDetails = await loadBirthDetails();
        console.log('📡 Loading yearly data for:', birthDetails?.name, 'year:', selectedYear);
        if (birthDetails) {
          // Fetch both in parallel
          await Promise.all([
            needsFetch ? fetchDetailedYearlyReport(birthDetails, selectedYear) : null,
            needsEvents ? fetchYearlyEvents(birthDetails, selectedYear) : null
          ]);
        } else {
          console.log('⚠️ No birth details found for yearly report');
        }
      };
      loadYearlyData();
    }
  }, [view, selectedYear, detailedYearlyReport, yearlyEventsData, loadingYearlyReport, loadingYearlyEvents, fetchDetailedYearlyReport, fetchYearlyEvents]);

  // Fetch detailed monthly predictions when month view is shown
  useEffect(() => {
    // Always try to fetch if we don't have LLM-quality data
    const needsFetch = !detailedMonthlyData || !detailedMonthlyData.daily_predictions;
    
    if (view === 'monthly' && needsFetch && !loadingMonthlyDetailed) {
      const loadMonthlyDetailed = async () => {
        const birthDetails = await loadBirthDetails();
        console.log('📡 Loading monthly detailed for:', birthDetails?.name, selectedMonth + 1, selectedYear);
        if (birthDetails) {
          await fetchDetailedMonthlyPredictions(birthDetails, selectedMonth, selectedYear);
        } else {
          console.log('⚠️ No birth details found for monthly predictions');
        }
      };
      loadMonthlyDetailed();
    }
  }, [view, selectedMonth, selectedYear, detailedMonthlyData, loadingMonthlyDetailed, fetchDetailedMonthlyPredictions]);

  // Force fetch on initial mount if user has birth data - USE CACHE FIRST
  useEffect(() => {
    const initialFetch = async () => {
      // Check if we already have cached data - skip API calls if so
      const hasRealTimeline = !!realTimelineData;
      const hasYearly = !!detailedYearlyReport?.full_report;
      const hasMonthly = !!detailedMonthlyData?.daily_predictions;
      
      if (hasRealTimeline && hasYearly && hasMonthly) {
        console.log('✅ EventTimeline: All data cached, skipping initial fetch');
        setLoading(false);
        return;
      }
      
      const birthDetails = await loadBirthDetails();
      if (birthDetails) {
        console.log('🚀 Initial fetch for user:', birthDetails.name);
        
        // Only fetch what's missing - in parallel
        const fetchTasks = [];
        if (!hasRealTimeline) fetchTasks.push(fetchRealTimelinePredictions(birthDetails, selectedMonth, selectedYear));
        if (!hasYearly) fetchTasks.push(fetchDetailedYearlyReport(birthDetails, selectedYear));
        if (!hasMonthly) fetchTasks.push(fetchDetailedMonthlyPredictions(birthDetails, selectedMonth, selectedYear));
        
        if (fetchTasks.length > 0) {
          await Promise.all(fetchTasks);
        }
      } else {
        console.log('⚠️ No birth details found for timeline - setting empty state');
        setDetailedYearlyReport({ full_report: 'No birth data available. Please complete your birth details first.', dasha: {}, native: {} });
        setDetailedMonthlyData({ daily_details: [], daily_predictions: {}, key_days: { favorable: [], challenging: [] }, monthly_overview: 'No birth data available.' });
      }
    };
    initialFetch();
  }, []); // Only on mount

  // Generate REAL events for each day of the month from API data
  const generateMonthEvents = () => {
    const daysInMonth = new Date(selectedYear, selectedMonth + 1, 0).getDate();
    const monthEvents = {};
    const currentDate = new Date();
    const isCurrentMonth = currentDate.getMonth() === selectedMonth && currentDate.getFullYear() === selectedYear;
    
    // Initialize all days
    for (let d = 1; d <= daysInMonth; d++) {
      monthEvents[d] = [];
    }
    
    // PRIORITY 1: USE COMPUTED daily_details (v3 with SD + Moon transit)
    if (detailedMonthlyData?.daily_details && detailedMonthlyData?.daily_details.length > 0) {
      console.log('📅 Using COMPUTED daily_details with SD + Moon transit');
      
      detailedMonthlyData.daily_details.forEach((dayData) => {
        const dayNum = dayData.day;
        const quality = dayData.quality; // 'highly_favorable', 'favorable', 'mixed', 'challenging', 'difficult'
        const qualityScore = dayData.quality_score;
        const sdLord = dayData.dasha_active?.sd;
        const pdLord = dayData.dasha_active?.pd;
        const moonSign = dayData.moon_transit?.sign;
        const moonHouse = dayData.moon_transit?.house_from_lagna;
        const moonNak = dayData.moon_transit?.nakshatra;
        
        // Determine quality color
        const qualityColors = {
          'highly_favorable': '#22c55e',  // Green
          'favorable': '#84cc16',         // Lime
          'mixed': '#f59e0b',             // Amber
          'challenging': '#ef4444',       // Red
          'difficult': '#dc2626'          // Dark red
        };
        
        // Convert quality to user-friendly description
        const qualityDescriptions = {
          'highly_favorable': 'Excellent day for important activities',
          'favorable': 'Good energy for progress',
          'mixed': 'Balance activity with rest',
          'challenging': 'Take extra care today',
          'difficult': 'Focus on self-care'
        };
        
        // Add day quality indicator FIRST - USER FRIENDLY
        monthEvents[dayNum].push({
          domain: 'quality',
          color: qualityColors[quality] || '#6b7280',
          name: `${quality.replace('_', ' ').toUpperCase()}`,
          event: qualityDescriptions[quality] || 'Regular day',
          level: quality,
          type: 'day_quality',
          qualityScore: qualityScore
        });
        
        // Add domain events from computed data - NOW WITH MULTIPLE EVENTS
        if (dayData.domain_events) {
          Object.entries(dayData.domain_events).forEach(([domainKey, eventData]) => {
            const domain = DOMAINS.find(d => d.key === domainKey);
            if (domain && eventData) {
              // Support multiple events per domain
              const allEvents = eventData.all_events || [eventData.text];
              allEvents.forEach((eventText, idx) => {
                if (eventText) {
                  monthEvents[dayNum].push({
                    domain: domainKey,
                    color: domain.color,
                    name: domain.name,
                    event: eventText,
                    level: eventData.quality || quality,
                    type: 'computed_prediction',
                    sdInfluence: eventData.sd_influence,
                    moonHouse: eventData.moon_house,
                    isPrimary: idx === 0
                  });
                }
              });
            }
          });
        }
      });
      
      return monthEvents;
    }
    
    // PRIORITY 2: USE DETAILED LLM PREDICTIONS (older format)
    if (detailedMonthlyData?.daily_predictions && Object.keys(detailedMonthlyData.daily_predictions).length > 0) {
      console.log('📅 Using DETAILED LLM daily predictions');
      const dailyPreds = detailedMonthlyData.daily_predictions;
      
      Object.entries(dailyPreds).forEach(([day, domains]) => {
        const dayNum = parseInt(day);
        if (domains && typeof domains === 'object') {
          Object.entries(domains).forEach(([domainKey, prediction]) => {
            const domain = DOMAINS.find(d => d.key === domainKey);
            if (domain && prediction) {
              monthEvents[dayNum].push({
                domain: domainKey,
                color: domain.color,
                name: domain.name,
                event: prediction,
                level: 'detailed',
                type: 'llm_prediction'
              });
            }
          });
        }
      });
      
      return monthEvents;
    }
    
    // USE REAL PREDICTIONS FROM API if available
    if (monthlyPredictions?.daily_predictions) {
      console.log('📅 Using REAL daily predictions from API');
      Object.entries(monthlyPredictions.daily_predictions).forEach(([day, predictions]) => {
        const dayNum = parseInt(day);
        if (predictions && Array.isArray(predictions)) {
          predictions.forEach(pred => {
            const domain = DOMAINS.find(d => d.key === pred.domain);
            if (domain) {
              monthEvents[dayNum].push({
                domain: pred.domain,
                color: pred.color || domain.color,
                name: pred.name || domain.name,
                event: pred.prediction,
                level: pred.level || pred.type,
                type: pred.type || 'prediction',
                rulingPlanets: pred.ruling_planets
              });
            }
          });
        }
      });
      
      // Add key days from API - NO EMOJIS
      if (monthlyPredictions?.key_days) {
        monthlyPredictions.key_days.forEach(keyDay => {
          const dayNum = keyDay.day;
          if (!monthEvents[dayNum].some(e => e.type === 'key_day')) {
            monthEvents[dayNum].push({
              domain: 'dasha',
              color: keyDay.type === 'favorable' ? '#22c55e' : '#f59e0b',
              name: 'Dasha Period',
              event: keyDay.reason,
              level: keyDay.type,
              type: 'key_day'
            });
          }
        });
      }
      
      return monthEvents;
    }
    
    // FALLBACK: Use domain insights from API or old data
    const safeArray = (val) => Array.isArray(val) ? val : [];
    
    DOMAINS.forEach(domain => {
      // Use domain insights from API if available
      const apiInsight = domainInsights?.[domain.key];
      const data = allDomainsData[domain.key] || {};
      const insights = data.insights || {};
      const monthly = data.monthly || {};
      
      // Extract predictions based on API data or fallback
      let positiveThemes = [];
      let challenges = [];
      
      if (apiInsight) {
        // Use API data
        positiveThemes = [apiInsight.mahadasha_effect, apiInsight.antardasha_effect].filter(Boolean);
        challenges = apiInsight.level === 'challenging' ? [apiInsight.pratyantardasha_effect] : [];
      } else {
        // Fallback to domain-specific defaults
        if (domain.key === 'health') {
          positiveThemes = safeArray(insights.positive_periods || ['Energy balanced', 'Vitality period']);
          challenges = safeArray(insights.disease_risks || ['Care needed']);
        } else if (domain.key === 'career') {
          const yogas = safeArray(data.career_yogas);
          positiveThemes = yogas.map(y => y.effect || y.name).filter(Boolean);
          if (positiveThemes.length === 0) positiveThemes = ['Progress expected'];
        } else if (domain.key === 'wealth') {
          positiveThemes = ['Financial opportunity'];
          challenges = ['Budget awareness'];
        } else if (domain.key === 'marriage') {
          positiveThemes = safeArray(insights.positive_aspects || ['Harmony']);
          challenges = safeArray(insights.challenges || ['Patience']);
        } else {
          positiveThemes = [`${domain.name} growth period`];
          challenges = [`${domain.name} requires focus`];
        }
      }
      
      // Assign events to key days based on dasha hierarchy
      const keyDays = [1, 4, 7, 11, 14, 18, 21, 25, 28].filter(d => d <= daysInMonth);
      
      keyDays.forEach((day, idx) => {
        const isPositive = apiInsight?.level !== 'challenging' || idx % 3 !== 2;
        const predictions = isPositive ? positiveThemes : challenges;
        const prediction = predictions[idx % Math.max(predictions.length, 1)];
        
        if (prediction) {
          monthEvents[day].push({
            domain: domain.key,
            color: domain.color,
            name: domain.name,
            event: prediction,
            level: isPositive ? 'positive' : 'caution',
            type: 'prediction'
          });
        }
      });
      
      // Add today's prediction if current month
      if (isCurrentMonth) {
        const today = currentDate.getDate();
        const todayAdvice = apiInsight?.summary || insights.today_advice || 
          `${domain.name} focus day`;
        
        const hasTodayEvent = monthEvents[today].some(e => e.domain === domain.key);
        if (!hasTodayEvent) {
          monthEvents[today].push({
            domain: domain.key,
            color: domain.color,
            name: domain.name,
            event: todayAdvice,
            level: 'today',
            type: 'today'
          });
        }
      }
    });
    
    return monthEvents;
  };
  
  const monthEvents = generateMonthEvents();

  // Get yearly overview for all 8 domains with REAL API predictions
  const getYearlyOverview = () => {
    // USE REAL DATA FROM API if available
    if (domainInsights && Object.keys(domainInsights).length > 0) {
      console.log('📊 Using REAL yearly insights from API');
      return DOMAINS.map(domain => {
        const apiInsight = domainInsights[domain.key];
        if (!apiInsight) {
          return {
            ...domain,
            level: 'moderate',
            mainPrediction: `${domain.name} outlook for ${selectedYear}`,
            predictions: []
          };
        }
        
        const predictions = [];
        
        // Add personalized chart-specific predictions FIRST
        const personalized = apiInsight.personalized || {};
        if (personalized.favorable) {
          predictions.push({ 
            text: personalized.favorable, 
            type: 'positive', 
            timing: 'Chart Strength' 
          });
        }
        if (personalized.challenging && personalized.challenging !== personalized.favorable) {
          predictions.push({ 
            text: personalized.challenging, 
            type: 'caution', 
            timing: 'Watch For' 
          });
        }
        
        // Add dasha-based predictions from API
        if (apiInsight.mahadasha_effect) {
          predictions.push({ 
            text: apiInsight.mahadasha_effect, 
            type: apiInsight.level === 'favorable' ? 'positive' : apiInsight.level === 'challenging' ? 'caution' : 'info', 
            timing: `${dashaHierarchy?.mahadasha?.lord || 'Current'} Mahadasha` 
          });
        }
        
        if (apiInsight.antardasha_effect) {
          predictions.push({ 
            text: apiInsight.antardasha_effect, 
            type: 'dasha', 
            timing: `${dashaHierarchy?.antardasha?.lord || 'Current'} Antardasha` 
          });
        }
        
        if (apiInsight.pratyantardasha_effect) {
          predictions.push({ 
            text: apiInsight.pratyantardasha_effect, 
            type: 'advice', 
            timing: `${dashaHierarchy?.pratyantardasha?.lord || 'Current'} Pratyantardasha` 
          });
        }
        
        // Add yearly monthly breakdown if available
        if (yearlyOverviewData?.monthly_overview) {
          yearlyOverviewData.monthly_overview.slice(0, 3).forEach(month => {
            predictions.push({
              text: `${month.month_name}: ${month.theme}`,
              type: 'timing',
              timing: `${month.dasha}`
            });
          });
        }
        
        return {
          ...domain,
          level: apiInsight.level || 'moderate',
          mainPrediction: personalized.favorable || apiInsight.summary || `${domain.name} outlook for ${selectedYear}`,
          predictions: predictions.slice(0, 10)
        };
      });
    }
    
    // FALLBACK: Use existing logic
    return DOMAINS.map(domain => {
      const data = allDomainsData[domain.key] || {};
      const yearly = data.yearly || {};
      const insights = data.insights || {};
      const monthly = data.monthly || {};
      
      // Helper to safely get array
      const safeArray = (val) => Array.isArray(val) ? val : [];
      
      // Extract ALL predictions from V3 algorithm data
      const predictions = [];
      let level = 'moderate';
      let mainPrediction = '';
      
      // ===== HEALTH =====
      if (domain.key === 'health') {
        const healthEvents = safeArray(yearly.events || yearly.health_events || data.health_events);
        const diseases = safeArray(insights.diseases || insights.disease_risks);
        const bodyParts = safeArray(insights.body_parts || insights.focus_areas);
        const vitality = insights.vitality || {};
        const constitution = insights.constitution;
        const bodySystems = safeArray(insights.body_systems);
        const strongAreas = safeArray(insights.strong_areas);
        const sensitiveAreas = safeArray(insights.sensitive_areas);
        const organVulnerabilities = safeArray(insights.organ_vulnerabilities);
        const mahadashas = safeArray(insights.all_mahadashas || data.all_mahadashas);
        const currentPeriod = insights.current_period || {};
        
        // Constitution
        if (constitution) {
          predictions.push({ text: `Constitution Type: ${constitution}`, type: 'info', timing: 'Inherent nature' });
        }
        
        // Strong areas
        if (strongAreas.length > 0) {
          predictions.push({ text: `Strong: ${strongAreas.slice(0, 3).join(', ')}`, type: 'positive', timing: 'Natural strength' });
          level = 'good';
        }
        
        // Sensitive areas
        if (sensitiveAreas.length > 0) {
          predictions.push({ text: `Care needed: ${sensitiveAreas.slice(0, 3).join(', ')}`, type: 'caution', timing: 'Focus areas' });
        }
        
        // Body systems
        bodySystems.slice(0, 4).forEach(sys => {
          if (sys && sys.name) {
            const isStrong = sys.status === 'strong' || sys.status === 'balanced';
            predictions.push({ 
              text: `${sys.name}: ${sys.description || sys.status}`, 
              type: isStrong ? 'positive' : 'caution', 
              timing: sys.status 
            });
          }
        });
        
        // Organ vulnerabilities
        organVulnerabilities.slice(0, 3).forEach(vuln => {
          if (vuln && vuln.planet) {
            predictions.push({ 
              text: `${vuln.planet} affects ${(vuln.organs || []).join(', ')}`, 
              type: vuln.risk_level === 'attention' ? 'caution' : 'advice', 
              timing: `Risk: ${vuln.risk_level}` 
            });
          }
        });
        
        // Current dasha health impact
        if (currentPeriod.mahadasha) {
          predictions.push({ 
            text: `Current ${currentPeriod.mahadasha} Dasha - ${(currentPeriod.health_themes || []).slice(0, 2).join(', ') || 'General wellness'}`, 
            type: 'dasha', 
            timing: 'Active period' 
          });
        }
        
        // Health events for the year
        healthEvents.slice(0, 4).forEach(evt => {
          if (evt && (evt.title || evt.description)) {
            predictions.push({ text: evt.title || evt.description, type: evt.severity || 'info', timing: evt.timing || '2025' });
          }
        });
        
        // Vitality
        if (vitality.overall === 'HIGH' || vitality.overall === 'excellent') {
          level = 'excellent';
        }
        
        mainPrediction = vitality.summary || `Health focus: ${bodyParts[0] || constitution || 'General wellness'}`;
      }
      
      // ===== CAREER =====
      if (domain.key === 'career') {
        const careerSummary = data.career_summary || {};
        const careerVerdict = data.career_verdict || {};
        const yogas = safeArray(data.career_yogas);
        const domain1 = data.domain_1_identity || {};
        const domain3 = data.domain_3_employment || {};
        const domain4 = data.domain_4_growth || {};
        const domain5 = data.domain_5_timing || {};
        const domain6 = data.domain_6_profession || {};
        const domain9 = data.domain_9_foreign || {};
        const mahadashas = safeArray(data.all_mahadashas);
        
        // Career verdict
        if (careerVerdict.one_line_verdict) {
          mainPrediction = careerVerdict.one_line_verdict;
        }
        
        // Primary path
        if (careerVerdict.primary_path) {
          predictions.push({ text: `Primary Path: ${careerVerdict.primary_path}`, type: 'strength', timing: 'Career direction' });
        }
        
        // Career destiny class
        if (careerVerdict.career_destiny_class) {
          predictions.push({ text: `Career Type: ${careerVerdict.career_destiny_class}`, type: 'info', timing: 'Destiny' });
        }
        
        // Best suited fields
        if (careerVerdict.best_suited_fields) {
          const fields = Array.isArray(careerVerdict.best_suited_fields) ? careerVerdict.best_suited_fields.slice(0, 3).join(', ') : careerVerdict.best_suited_fields;
          predictions.push({ text: `Best Fields: ${fields}`, type: 'opportunity', timing: 'Optimal sectors' });
        }
        
        // Job vs Business
        const jobBusiness = careerVerdict.job_vs_business || domain3['3.1_job_vs_business'] || {};
        if (jobBusiness.verdict || jobBusiness.recommendation) {
          predictions.push({ text: `Job vs Business: ${jobBusiness.verdict || jobBusiness.recommendation}`, type: 'advice', timing: 'Career choice' });
        }
        
        // Growth speed
        const growth = domain4['4.1_growth_speed'] || {};
        if (growth.type) {
          predictions.push({ text: `Growth: ${growth.type} pace`, type: growth.type === 'rapid' ? 'positive' : 'info', timing: `Accelerates at age ${growth.acceleration_age || 30}` });
        }
        
        // Fame & recognition
        const fame = domain4['4.4_fame_visibility'] || {};
        if (fame.level) {
          predictions.push({ text: `Fame Level: ${fame.level}`, type: fame.level === 'high' ? 'positive' : 'info', timing: fame.type || 'Career recognition' });
        }
        
        // Foreign career
        const foreign = domain9['9.1_foreign_career'] || {};
        if (foreign.indicated) {
          predictions.push({ text: `Foreign Career: ${foreign.indicated}`, type: 'opportunity', timing: 'International prospects' });
        }
        
        // Career yogas
        yogas.slice(0, 3).forEach(yoga => {
          if (yoga && (yoga.name || yoga.yoga)) {
            predictions.push({ text: `${yoga.name || yoga.yoga}${yoga.result ? ` - ${yoga.result}` : ''}`, type: 'yoga', timing: 'Active blessing' });
          }
        });
        
        // Active Mahadasha career theme
        const activeMD = mahadashas.find(md => md && md.status === 'active');
        if (activeMD && activeMD.career_themes) {
          predictions.push({ text: `${activeMD.planet} Dasha: ${activeMD.career_themes.slice(0, 2).join(', ')}`, type: 'dasha', timing: `${activeMD.years}` });
        }
        
        level = careerVerdict.favorable ? 'excellent' : careerSummary.strength === 'Strong' ? 'good' : 'moderate';
      }
      
      // ===== WEALTH =====
      if (domain.key === 'wealth') {
        const wealthClass = data.wealth_class || {};
        const wealthScore = data.wealth_score || 0;
        const billionaireScope = data.billionaire_scope || {};
        const businessVsJob = data.business_vs_job || {};
        const propertyAnalysis = data.property_analysis || {};
        const induLagna = data.indu_lagna || {};
        const horaLagna = data.hora_lagna || {};
        const wealthTiming = data.wealth_timing || {};
        const preconditions = data.preconditions || {};
        
        // Wealth class and score
        if (wealthClass.class || wealthClass.name) {
          mainPrediction = `Wealth Class: ${wealthClass.class || wealthClass.name}`;
          predictions.push({ text: wealthClass.description || `${wealthClass.class || wealthClass.name} tier wealth potential`, type: 'info', timing: 'Lifetime ceiling' });
        }
        
        if (wealthScore) {
          predictions.push({ text: `Wealth Score: ${wealthScore}/100`, type: wealthScore > 70 ? 'positive' : 'info', timing: 'Overall potential' });
          level = wealthScore > 80 ? 'excellent' : wealthScore > 60 ? 'good' : 'moderate';
        }
        
        // Billionaire scope
        if (billionaireScope.scope || billionaireScope.criteria_met) {
          predictions.push({ text: `Billionaire Potential: ${billionaireScope.scope || 'Assessed'}`, type: 'wealth', timing: `${billionaireScope.criteria_met || '?'}/8 criteria met` });
        }
        
        // Business vs Job
        if (businessVsJob.verdict || businessVsJob.recommendation) {
          predictions.push({ text: `Income Path: ${businessVsJob.verdict || businessVsJob.recommendation}`, type: 'advice', timing: 'Recommended' });
        }
        
        // Property
        const property = propertyAnalysis.property || {};
        if (property.status) {
          predictions.push({ text: `Property: ${property.status}`, type: property.status === 'STRONG' ? 'positive' : 'info', timing: property.timing || 'Real estate' });
        }
        
        // Indu Lagna
        if (induLagna.sign) {
          predictions.push({ text: `Indu Lagna: ${induLagna.sign} (${induLagna.strength || 'moderate'})`, type: 'info', timing: 'Wealth indicator' });
        }
        
        // Wealth timing dashas
        const wealthDashas = safeArray(wealthTiming.all_dashas);
        const activeDasha = wealthDashas.find(d => d.is_wealth_dasha);
        if (activeDasha) {
          predictions.push({ text: `${activeDasha.lord} Dasha: ${activeDasha.wealth_effect?.effect || 'Wealth period'}`, type: 'dasha', timing: `Age ${Math.round(activeDasha.start_age)}-${Math.round(activeDasha.end_age)}` });
        }
      }
      
      // ===== MARRIAGE/RELATIONSHIPS =====
      if (domain.key === 'marriage') {
        const marriagePromise = insights.marriage_promise || {};
        const maritalHarmony = insights.marital_harmony || {};
        const spouseNature = insights.spouse_nature || {};
        const timing = insights.timing || {};
        const loveVsArranged = insights.love_vs_arranged || {};
        const divorceIndicators = insights.divorce_indicators || {};
        const marriageYogas = safeArray(insights.marriage_yogas);
        const mahadashas = safeArray(insights.all_mahadashas);
        
        // Marriage harmony level
        if (maritalHarmony.level) {
          mainPrediction = `Marital Harmony: ${maritalHarmony.level}`;
          predictions.push({ text: maritalHarmony.description || `${maritalHarmony.level} harmony indicated`, type: maritalHarmony.level === 'excellent' ? 'positive' : 'info', timing: 'Relationship quality' });
          level = maritalHarmony.level === 'excellent' ? 'excellent' : maritalHarmony.level === 'good' ? 'good' : 'moderate';
        }
        
        // Marriage promise
        if (marriagePromise.promise_strength) {
          predictions.push({ text: `Marriage Promise: ${marriagePromise.promise_strength}`, type: 'info', timing: 'Indication' });
        }
        
        // Marriage timing
        if (timing.likely_age_range) {
          predictions.push({ text: `Marriage Age: ${timing.likely_age_range}`, type: 'timing', timing: timing.timing_type || 'Expected' });
        }
        
        // Marriage dashas
        const marriageDashas = safeArray(timing.marriage_dashas);
        marriageDashas.slice(0, 2).forEach(md => {
          if (md && md.planet) {
            predictions.push({ text: `${md.planet} period favorable for marriage`, type: 'dasha', timing: md.age_range || 'Marriage dasha' });
          }
        });
        
        // Spouse nature
        if (spouseNature['7th_sign'] || spouseNature['7th_lord_nature']) {
          predictions.push({ text: `Spouse: ${spouseNature['7th_lord_nature'] || 'Compatible'} nature`, type: 'info', timing: spouseNature['7th_sign'] || 'Partner profile' });
        }
        
        // Love vs Arranged
        if (loveVsArranged.type) {
          predictions.push({ text: `Marriage Type: ${loveVsArranged.type}`, type: 'info', timing: loveVsArranged.description || 'Likely' });
        }
        
        // Divorce risk
        if (divorceIndicators.risk_level) {
          predictions.push({ text: `Stability: ${divorceIndicators.risk_level === 'low' ? 'Stable marriage indicated' : 'Some challenges'}`, type: divorceIndicators.risk_level === 'low' ? 'positive' : 'caution', timing: divorceIndicators.advice || 'Relationship' });
        }
        
        // Marriage yogas
        marriageYogas.slice(0, 2).forEach(yoga => {
          if (yoga && (yoga.name || typeof yoga === 'string')) {
            predictions.push({ text: yoga.name || yoga, type: 'yoga', timing: 'Marriage blessing' });
          }
        });
      }
      
      // ===== EDUCATION =====
      if (domain.key === 'education') {
        const educationSummary = data.education_summary || {};
        const domain1 = data.domain_1_psychology || {};
        const domain3 = data.domain_3_performance || {};
        const domain4 = data.domain_4_aptitude || {};
        const domain5 = data.domain_5_higher_education || {};
        const domain6 = data.domain_6_skills || {};
        const domain7 = data.domain_7_certifications || {};
        const mahadashas = safeArray(data.all_mahadashas);
        
        // Education type and planet
        if (educationSummary.education_type) {
          mainPrediction = `Education Type: ${educationSummary.education_type}`;
        }
        
        // Education planet strength
        if (educationSummary.mercury_strength || educationSummary.jupiter_strength) {
          const mercStr = educationSummary.mercury_strength || 'moderate';
          const jupStr = educationSummary.jupiter_strength || 'moderate';
          predictions.push({ text: `Mercury: ${mercStr}, Jupiter: ${jupStr}`, type: mercStr === 'strong' ? 'positive' : 'info', timing: 'Education planets' });
        }
        
        // Best field
        if (educationSummary.best_field) {
          predictions.push({ text: `Best Field: ${educationSummary.best_field}`, type: 'strength', timing: 'Academic strength' });
        }
        
        // Best subjects
        if (educationSummary.best_subjects && educationSummary.best_subjects.length > 0) {
          predictions.push({ text: `Strong in: ${educationSummary.best_subjects.slice(0, 3).join(', ')}`, type: 'positive', timing: 'Natural aptitude' });
        }
        
        // Education yogas
        if (educationSummary.yogas && educationSummary.yogas.length > 0) {
          educationSummary.yogas.slice(0, 2).forEach(yoga => {
            predictions.push({ text: yoga, type: 'yoga', timing: 'Education blessing' });
          });
          level = 'good';
        }
        
        // Intelligence type from domain 1
        const intType = domain1['1_intelligence_type'] || {};
        if (intType.value) {
          predictions.push({ text: `Intelligence: ${intType.value}`, type: 'info', timing: intType.detail || 'Mind type' });
        }
        
        // Learning style
        const learnStyle = domain1['2_learning_style'] || {};
        if (learnStyle.value) {
          predictions.push({ text: `Learning Style: ${learnStyle.value}`, type: 'advice', timing: learnStyle.detail || 'How you learn' });
        }
        
        // Aptitude analysis
        Object.entries(domain4).slice(0, 4).forEach(([key, val]) => {
          if (val && val.title && val.value) {
            predictions.push({ text: `${val.title}: ${val.value}`, type: val.value === 'High' ? 'positive' : 'info', timing: 'Aptitude' });
          }
        });
        
        // Higher education
        Object.entries(domain5).slice(0, 3).forEach(([key, val]) => {
          if (val && val.title && val.value) {
            predictions.push({ text: `${val.title}: ${val.value}`, type: 'info', timing: val.detail || 'Higher ed' });
          }
        });
      }
      
      // ===== SPIRITUAL =====
      if (domain.key === 'spiritual') {
        const domain1 = data.domain_1_psychology || {};
        const domain2 = data.domain_2_dharma || {};
        const domain3 = data.domain_3_guru || {};
        const domain4 = data.domain_4_practices || {};
        const domain5 = data.domain_5_meditation || {};
        const domain6 = data.domain_6_moksha || {};
        const domain7 = data.domain_7_karma || {};
        
        // Soul nature
        const soulNature = domain1['1_soul_nature'] || {};
        if (soulNature.value) {
          mainPrediction = `Soul Nature: ${soulNature.value}`;
        }
        
        // Dharma path
        const dharmaPath = domain2['12_dharmic_path'] || {};
        if (dharmaPath.value) {
          predictions.push({ text: `Dharmic Path: ${dharmaPath.value}`, type: 'guidance', timing: 'Life direction' });
        }
        
        // Life purpose
        const lifePurpose = domain2['11_life_purpose'] || {};
        if (lifePurpose.value) {
          predictions.push({ text: `Life Purpose: ${lifePurpose.value}`, type: 'info', timing: lifePurpose.detail || 'Dharma' });
        }
        
        // Faith foundation
        const faith = domain1['3_faith_foundation'] || {};
        if (faith.value) {
          predictions.push({ text: `Faith Foundation: ${faith.value}`, type: faith.value === 'Strong' ? 'positive' : 'info', timing: 'Spiritual base' });
        }
        
        // Guru connection
        const guru = domain3['21_guru_connection'] || {};
        if (guru.value) {
          predictions.push({ text: `Guru Connection: ${guru.value}`, type: guru.value === 'Strong' ? 'positive' : 'info', timing: 'Spiritual guidance' });
        }
        
        // Meditation aptitude
        const meditation = domain5['41_meditation_aptitude'] || {};
        if (meditation.value) {
          predictions.push({ text: `Meditation: ${meditation.value}`, type: 'advice', timing: meditation.detail || 'Inner practice' });
        }
        
        // Moksha potential
        const moksha = domain6['51_moksha_potential'] || {};
        if (moksha.value) {
          predictions.push({ text: `Liberation Potential: ${moksha.value}`, type: 'info', timing: 'Spiritual destiny' });
        }
        
        // Karmic load
        const karma = domain7['61_karmic_load'] || {};
        if (karma.value) {
          predictions.push({ text: `Karmic Load: ${karma.value}`, type: karma.value === 'Light' ? 'positive' : 'info', timing: 'Past life' });
        }
        
        level = 'moderate';
      }
      
      // ===== TRAVEL =====
      if (domain.key === 'travel') {
        const travelNature = insights.travel_nature;
        const settlementType = insights.settlement_type;
        const foreignIndicators = safeArray(insights.foreign_indicators);
        const travelPurposes = safeArray(insights.travel_purposes);
        const favorableDirections = safeArray(insights.favorable_directions);
        const travelCategories = safeArray(insights.travel_categories);
        const mahadashas = safeArray(insights.all_mahadashas);
        
        // Travel nature
        if (travelNature) {
          mainPrediction = travelNature;
        }
        
        // Settlement type
        if (settlementType) {
          predictions.push({ text: settlementType, type: 'info', timing: 'Settlement destiny' });
        }
        
        // Foreign indicators
        foreignIndicators.slice(0, 2).forEach(ind => {
          predictions.push({ text: ind, type: 'info', timing: 'Foreign prospects' });
        });
        
        // Travel purposes
        if (travelPurposes.length > 0) {
          predictions.push({ text: `Travel for: ${travelPurposes.slice(0, 3).join(', ')}`, type: 'advice', timing: 'Best purposes' });
        }
        
        // Favorable directions
        favorableDirections.slice(0, 2).forEach(dir => {
          if (dir && dir.direction) {
            predictions.push({ text: `${dir.direction}: ${dir.purpose?.replace(/_/g, ' ') || 'Favorable'}`, type: 'positive', timing: `${dir.planet} direction` });
          }
        });
        
        // Travel categories
        travelCategories.slice(0, 4).forEach(cat => {
          if (cat && cat.name && cat.status) {
            predictions.push({ text: `${cat.icon || ''} ${cat.name}: ${cat.status}`, type: cat.status === 'strong' ? 'positive' : 'info', timing: cat.description || 'Travel type' });
          }
        });
        
        // Active Mahadasha travel themes
        const activeMD = mahadashas.find(md => md && md.status === 'active');
        if (activeMD && activeMD.travel_themes) {
          predictions.push({ text: `${activeMD.planet} Dasha: ${activeMD.travel_themes.slice(0, 2).map(t => t.replace(/_/g, ' ')).join(', ')}`, type: 'dasha', timing: activeMD.years });
        }
        
        level = 'moderate';
      }
      
      // ===== FAMILY =====
      if (domain.key === 'family') {
        const familySummary = insights.family_summary || {};
        const childrenData = familySummary.children_indicators || {};
        const parentData = familySummary.parental_relations || {};
        const siblingData = familySummary.sibling_dynamics || {};
        const marriageFamily = familySummary.marriage_family || {};
        const familyYogas = safeArray(familySummary.family_yogas);
        const familyCategories = safeArray(insights.family_categories);
        const mahadashas = safeArray(insights.all_mahadashas);
        
        // Domestic harmony
        const domesticHarmony = familySummary.domestic_harmony;
        if (domesticHarmony) {
          mainPrediction = domesticHarmony;
        }
        
        // Family categories
        familyCategories.slice(0, 4).forEach(cat => {
          if (cat && cat.name && cat.status) {
            predictions.push({ text: `${cat.icon || ''} ${cat.name}: ${cat.description || cat.status}`, type: cat.status === 'strong' ? 'positive' : 'info', timing: `Status: ${cat.status}` });
          }
        });
        
        // Parent relations
        if (parentData.father_relation) {
          predictions.push({ text: `Father: ${parentData.father_relation.replace(/_/g, ' ')}`, type: parentData.father_relation.includes('excellent') ? 'positive' : 'info', timing: 'Paternal bond' });
        }
        if (parentData.mother_relation) {
          predictions.push({ text: `Mother: ${parentData.mother_relation.replace(/_/g, ' ')}`, type: parentData.mother_relation.includes('excellent') ? 'positive' : 'info', timing: 'Maternal bond' });
        }
        if (parentData.ancestral_blessings) {
          predictions.push({ text: 'Ancestral Blessings: Yes', type: 'positive', timing: 'Family fortune' });
        }
        
        // Sibling relations
        if (siblingData.sibling_support_overall) {
          predictions.push({ text: `Sibling Support: ${siblingData.sibling_support_overall}`, type: siblingData.sibling_support_overall === 'good' ? 'positive' : 'info', timing: 'Sibling bonds' });
        }
        
        // Children indicators
        if (childrenData.fertility_strength) {
          predictions.push({ text: `Fertility: ${childrenData.fertility_strength}`, type: childrenData.fertility_strength === 'strong' ? 'positive' : 'info', timing: 'Children blessing' });
        }
        if (childrenData.timing) {
          predictions.push({ text: `Children Timing: ${childrenData.timing.replace(/_/g, ' ')}`, type: 'timing', timing: 'Progeny' });
        }
        if (childrenData.jupiter_aspects_5th) {
          predictions.push({ text: 'Jupiter blesses 5th house', type: 'yoga', timing: 'Children blessing' });
        }
        
        // Marriage family harmony
        if (marriageFamily.marital_harmony) {
          predictions.push({ text: `Marital Harmony: ${marriageFamily.marital_harmony}`, type: marriageFamily.marital_harmony === 'excellent' ? 'positive' : 'info', timing: 'Spouse relations' });
        }
        
        // Family yogas
        familyYogas.slice(0, 2).forEach(yoga => {
          predictions.push({ text: yoga, type: 'yoga', timing: 'Family blessing' });
        });
        
        // Active Mahadasha family themes
        const activeMD = mahadashas.find(md => md && md.status === 'active');
        if (activeMD && activeMD.family_themes) {
          predictions.push({ text: `${activeMD.planet} Dasha: ${activeMD.family_themes.slice(0, 2).map(t => t.replace(/_/g, ' ')).join(', ')}`, type: 'dasha', timing: activeMD.years });
        }
        
        level = 'moderate';
      }
      
      // If no predictions found, use mahadasha info
      if (predictions.length === 0) {
        const mahadashas = safeArray(insights.all_mahadashas || data.all_mahadashas);
        const activeMD = mahadashas.find(md => md && (md.status === 'active' || md.status === 'current'));
        
        if (activeMD) {
          const themes = safeArray(activeMD[`${domain.key}_themes`] || activeMD.themes);
          themes.slice(0, 3).forEach(theme => {
            if (theme && typeof theme === 'string') {
              predictions.push({ text: theme, type: 'dasha', timing: `${activeMD.planet || activeMD.lord} Dasha` });
            }
          });
          mainPrediction = mainPrediction || `${activeMD.planet || activeMD.lord || 'Current'} Mahadasha active`;
        }
      }
      
      // ABSOLUTE FINAL FALLBACK: If still no predictions, generate from mainPrediction
      if (predictions.length === 0 && mainPrediction) {
        // Extract mahadasha lord from mainPrediction like "Venus Mahadasha active"
        const mdMatch = mainPrediction.match(/(\w+)\s+Mahadasha/i);
        const lord = mdMatch ? mdMatch[1] : 'Current';
        
        // Domain-specific predictions based on ruling planet
        const planetDomainPredictions = {
          'Sun': {
            health: ['Vitality peaks in summer months', 'Heart and spine need attention', 'Morning exercise highly beneficial'],
            career: ['Leadership opportunities arise', 'Government sector favorable', 'Authority figures support you'],
            wealth: ['Steady income from profession', 'Father may assist financially', 'Gold investments favorable'],
            marriage: ['Ego clashes possible if unmarried', 'Spouse seeks independence', 'Mutual respect strengthens bond'],
            education: ['Political science favored', 'Administrative studies beneficial', 'Competitive exams success'],
            spiritual: ['Self-realization journey begins', 'Temple visits beneficial', 'Surya mantras powerful'],
            travel: ['Official travels indicated', 'Eastern direction favorable', 'Sunday travels auspicious'],
            family: ['Father figure prominent', 'Family reputation rises', 'Leadership role in family']
          },
          'Moon': {
            health: ['Mental wellness priority', 'Hydration important', 'Sleep quality affects overall health'],
            career: ['Public-facing roles succeed', 'Creative fields flourish', 'Emotional intelligence helps'],
            wealth: ['Variable income patterns', 'Real estate opportunities', 'Silver investments favorable'],
            marriage: ['Emotional bonding deepens', 'Nurturing energy flows', 'Mother-in-law relations matter'],
            education: ['Psychology studies favored', 'Arts and humanities shine', 'Memory power increases'],
            spiritual: ['Intuition heightens', 'Moon meditations powerful', 'Devotional practices benefit'],
            travel: ['Coastal destinations favorable', 'Monday travels auspicious', 'Water-related journeys'],
            family: ['Mother bond strengthens', 'Home improvements likely', 'Family gatherings increase']
          },
          'Mars': {
            health: ['High energy levels', 'Watch for accidents', 'Sports and exercise essential'],
            career: ['Technical achievements likely', 'Defense and engineering favored', 'Assertiveness pays off'],
            wealth: ['Property gains possible', 'Real estate investments', 'Sudden expenses on vehicles'],
            marriage: ['Passion in relationships', 'Arguments need management', 'Physical compatibility matters'],
            education: ['Engineering and technical studies', 'Sports achievements', 'Competitive spirit helps'],
            spiritual: ['Hanuman worship beneficial', 'Energy practices powerful', 'Courage in spiritual path'],
            travel: ['Adventure travel indicated', 'Southern direction favorable', 'Tuesday travels significant'],
            family: ['Sibling matters active', 'Property disputes possible', 'Courage protects family']
          },
          'Mercury': {
            health: ['Nervous system needs care', 'Skin health important', 'Mental stimulation essential'],
            career: ['Communication success', 'Business negotiations favorable', 'Writing and media shine'],
            wealth: ['Trading profits likely', 'Multiple income sources', 'Quick financial decisions work'],
            marriage: ['Intellectual compatibility key', 'Communication improves bonds', 'Friendship in marriage'],
            education: ['Languages and commerce favored', 'Analytical skills develop', 'Certifications beneficial'],
            spiritual: ['Mantra practice powerful', 'Vishnu worship beneficial', 'Knowledge as spiritual path'],
            travel: ['Business trips frequent', 'Short journeys productive', 'Wednesday travels favorable'],
            family: ['Cousins and relatives active', 'Communication heals rifts', 'Intellectual family bonds']
          },
          'Jupiter': {
            health: ['Watch weight and liver', 'Overall protection from illness', 'Optimism aids healing'],
            career: ['Teaching and advisory roles', 'Expansion in profession', 'Wisdom recognized at work'],
            wealth: ['Financial expansion phase', 'Investments grow', 'Generosity brings more'],
            marriage: ['Auspicious period for marriage', 'Spouse brings luck', 'Children blessings possible'],
            education: ['Higher studies blessed', 'Philosophy and law favored', 'Guru guidance available'],
            spiritual: ['Guru grace active', 'Pilgrimage beneficial', 'Wisdom deepens naturally'],
            travel: ['Pilgrimage journeys indicated', 'Northern direction favorable', 'Thursday travels blessed'],
            family: ['Children bring joy', 'Family expansion possible', 'Elders bless the home']
          },
          'Venus': {
            health: ['Reproductive health focus', 'Beauty and aesthetics matter', 'Comfort aids healing'],
            career: ['Creative arts flourish', 'Luxury industries favorable', 'Partnerships beneficial'],
            wealth: ['Material comforts increase', 'Luxury purchases likely', 'Spouse may bring wealth'],
            marriage: ['Romance peaks this period', 'Harmony in relationships', 'Beauty attracts love'],
            education: ['Fine arts and music shine', 'Design studies favored', 'Aesthetic sense develops'],
            spiritual: ['Bhakti yoga powerful', 'Lakshmi worship beneficial', 'Beauty as divine path'],
            travel: ['Leisure journeys indicated', 'Hill stations favorable', 'Friday travels romantic'],
            family: ['Spouse harmony key', 'Home beautification likely', 'Family celebrations increase']
          },
          'Saturn': {
            health: ['Bones and joints attention', 'Chronic issues may surface', 'Discipline in health routines'],
            career: ['Slow but steady progress', 'Hard work recognized', 'Long-term projects succeed'],
            wealth: ['Delayed but lasting gains', 'Frugality recommended', 'Real estate for long-term'],
            marriage: ['Patience in relationships', 'Karmic bonds manifest', 'Maturity in love matters'],
            education: ['Technical disciplines excel', 'Research and depth favored', 'Perseverance in studies'],
            spiritual: ['Karma yoga path active', 'Shani worship beneficial', 'Discipline as practice'],
            travel: ['Limited but purposeful travel', 'Western direction significant', 'Saturday travels karmic'],
            family: ['Elder responsibilities increase', 'Ancestral property matters', 'Family karma surfaces']
          },
          'Rahu': {
            health: ['Unusual health patterns', 'Modern treatments help', 'Anxiety management needed'],
            career: ['Foreign opportunities shine', 'Technology sector favorable', 'Unconventional paths succeed'],
            wealth: ['Sudden gains possible', 'Speculation risky', 'Foreign income likely'],
            marriage: ['Unconventional relationships', 'Foreign spouse possible', 'Past karma in love'],
            education: ['Research and innovation', 'Foreign education favorable', 'Occult studies attract'],
            spiritual: ['Tantric practices powerful', 'Past life themes surface', 'Shadow work beneficial'],
            travel: ['Foreign lands calling', 'Unexpected journeys', 'Northwestern direction significant'],
            family: ['Unusual family dynamics', 'In-laws from afar', 'Breaking family patterns']
          },
          'Ketu': {
            health: ['Spiritual healing needed', 'Mysterious ailments possible', 'Alternative medicine helps'],
            career: ['Behind-scenes work', 'Research excellence', 'Detachment from ambition'],
            wealth: ['Detachment from money', 'Spiritual investments', 'Unexpected expenses'],
            marriage: ['Karmic relationships resolve', 'Past life partners', 'Spiritual union possible'],
            education: ['Mystical subjects attract', 'Intuitive learning', 'Ancient knowledge calls'],
            spiritual: ['Liberation focus strong', 'Meditation deepens', 'Moksha path opens'],
            travel: ['Pilgrimages indicated', 'Southern direction karmic', 'Spiritual journeys'],
            family: ['Ancestral healing needed', 'Family karma resolves', 'Detachment with love']
          },
          'Current': {
            health: ['Focus on overall wellness', 'Regular checkups recommended', 'Balance in lifestyle helps'],
            career: ['Steady professional growth', 'Skills development important', 'Networking helps career'],
            wealth: ['Balanced financial approach', 'Savings important', 'Avoid impulsive spending'],
            marriage: ['Communication is key', 'Understanding deepens bonds', 'Quality time matters'],
            education: ['Consistent efforts pay off', 'Skill building recommended', 'Learning never stops'],
            spiritual: ['Daily practice beneficial', 'Inner peace priority', 'Gratitude helps growth'],
            travel: ['Planned trips favorable', 'Travel expands horizons', 'New places refresh mind'],
            family: ['Family time important', 'Support loved ones', 'Home brings comfort']
          }
        };
        
        const domainPreds = planetDomainPredictions[lord]?.[domain.key] || planetDomainPredictions['Current'][domain.key];
        domainPreds.forEach((pred, idx) => {
          predictions.push({ 
            text: pred, 
            type: idx === 0 ? 'positive' : idx === 1 ? 'advice' : 'info', 
            timing: `${lord} Mahadasha` 
          });
        });
      }
      
      return {
        ...domain,
        level,
        mainPrediction: mainPrediction || `${domain.name} outlook for ${selectedYear}`,
        predictions: predictions.slice(0, 10), // Max 10 predictions per domain for more detail
      };
    });
  };

  // Get level color
  const getLevelStyle = (level) => {
    const styles = {
      excellent: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', dot: 'bg-emerald-400' },
      favorable: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', dot: 'bg-emerald-400' },
      good: { bg: 'bg-teal-500/10', text: 'text-teal-400', dot: 'bg-teal-400' },
      moderate: { bg: 'bg-amber-500/10', text: 'text-amber-400', dot: 'bg-amber-400' },
      careful: { bg: 'bg-orange-500/10', text: 'text-orange-400', dot: 'bg-orange-400' },
      rest: { bg: 'bg-rose-500/10', text: 'text-rose-400', dot: 'bg-rose-400' },
      challenging: { bg: 'bg-rose-500/10', text: 'text-rose-400', dot: 'bg-rose-400' },
      settled: { bg: 'bg-violet-500/10', text: 'text-violet-400', dot: 'bg-violet-400' },
      today: { bg: 'bg-blue-500/10', text: 'text-blue-400', dot: 'bg-blue-400' },
    };
    return styles[level] || { bg: 'bg-slate-500/10', text: 'text-slate-400', dot: 'bg-slate-400' };
  };

  // Unified styling constants - matching Insights section
  const STYLES = {
    text: { primary: 'rgba(255,255,255,0.9)', secondary: 'rgba(255,255,255,0.65)', muted: 'rgba(255,255,255,0.4)' },
    accent: '#74B3FF',
    bg: { card: 'rgba(18,24,36,0.6)', hover: 'rgba(116,179,255,0.04)', border: 'rgba(116,179,255,0.08)' },
    fontSize: { sm: '12px', base: '13px', lg: '14px' }
  };

  const renderMonthlyView = () => {
    const firstDay = new Date(selectedYear, selectedMonth, 1).getDay();
    const daysInMonth = new Date(selectedYear, selectedMonth + 1, 0).getDate();
    const today = new Date();
    const isCurrentMonth = today.getMonth() === selectedMonth && today.getFullYear() === selectedYear;
    
    // If loading, show spinner
    if (loadingMonthlyDetailed) {
      return (
        <div className="flex flex-col items-center justify-center h-64">
          <div className="w-5 h-5 border border-white/20 border-t-white/50 rounded-full animate-spin" />
          <p className="text-white/40 text-xs mt-4">Computing daily predictions...</p>
        </div>
      );
    }
    
    // If no computed data and no birth details, show message to enter birth data
    if (!detailedMonthlyData?.daily_details || detailedMonthlyData.daily_details.length === 0) {
      // Check if this is because of no birth data
      if (detailedMonthlyData?.monthly_overview === 'No birth data available.') {
        return (
          <div className="flex flex-col items-center justify-center h-64 px-8 text-center">
            <p className="text-white/60 text-sm mb-2">No Birth Data Found</p>
            <p className="text-white/40 text-xs mb-4">Please complete your birth details to see timeline predictions.</p>
            <button 
              onClick={() => window.location.href = '/'}
              className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white/70 text-xs transition"
            >
              Enter Birth Details
            </button>
          </div>
        );
      }
      
      // Check if we're still loading or if we've finished but got no data
      if (loadingMonthlyDetailed) {
        return (
          <div className="flex flex-col items-center justify-center h-64">
            <div className="w-5 h-5 border border-white/20 border-t-white/50 rounded-full animate-spin" />
            <p className="text-white/40 text-xs mt-4">Loading monthly predictions...</p>
            <p className="text-white/30 text-[10px] mt-2">Computing SD and Moon transits</p>
          </div>
        );
      }
      
      // Finished loading but no data - auto-retry once, then show retry option
      if (!retryAttemptedRef.current) {
        retryAttemptedRef.current = true;
        setTimeout(() => refreshPredictions(), 2000);
        return (
          <div className="flex flex-col items-center justify-center h-64">
            <div className="w-5 h-5 border border-white/20 border-t-white/50 rounded-full animate-spin" />
            <p className="text-white/40 text-xs mt-4">Computing your monthly predictions...</p>
            <p className="text-white/30 text-[10px] mt-2">This may take a moment</p>
          </div>
        );
      }
      return (
        <div className="flex flex-col items-center justify-center h-64 px-8 text-center">
          <p className="text-white/60 text-sm mb-2">Predictions Loading</p>
          <p className="text-white/40 text-xs mb-4">Computing your timeline data...</p>
          <button 
            onClick={() => { retryAttemptedRef.current = false; refreshPredictions(); }}
            className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white/70 text-xs transition"
          >
            Refresh Data
          </button>
        </div>
      );
    }
    
    const monthEvents = generateMonthEvents();

    return (
      <div style={{ padding: '0 16px' }}>
        
        
        {/* KEY DAYS SUMMARY - Shows computed favorable and challenging days */}
        {detailedMonthlyData?.key_days && (
          <div style={{ background: STYLES.bg.card, borderRadius: 'clamp(10px, 3vw, 16px)', border: `1px solid ${STYLES.bg.border}`, padding: 'clamp(10px, 2.5vw, 16px)', marginBottom: '16px' }}>
            <p style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 'clamp(12px, 3.2vw, 14px)',
              color: '#F3E5AB',
              fontWeight: 400,
              textTransform: 'uppercase',
              letterSpacing: '0.18em',
              marginBottom: '12px',
            }}>
              Key Days This Month
            </p>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {/* Favorable Days */}
              <div style={{ background: 'rgba(34, 197, 94, 0.1)', borderRadius: '10px', padding: '12px', border: '1px solid rgba(34, 197, 94, 0.2)' }}>
                <p style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', color: '#22c55e', fontWeight: '600', marginBottom: '8px' }}>
                  Best Days
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {detailedMonthlyData.key_days.favorable?.slice(0, 5).map((fd, idx) => (
                    <button 
                      key={idx}
                      onClick={() => setSelectedDay(fd.day)}
                      style={{
                        padding: '4px 10px',
                        borderRadius: '6px',
                        background: 'rgba(34, 197, 94, 0.2)',
                        border: 'none',
                        color: '#22c55e',
                        fontSize: 'clamp(10px, 2.8vw, 12px)',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      {fd.day}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Challenging Days */}
              <div style={{ background: 'rgba(239, 68, 68, 0.1)', borderRadius: '10px', padding: '12px', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
                <p style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', color: '#ef4444', fontWeight: '600', marginBottom: '8px' }}>
                  Caution Days
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {detailedMonthlyData.key_days.challenging?.slice(0, 5).map((cd, idx) => (
                    <button 
                      key={idx}
                      onClick={() => setSelectedDay(cd.day)}
                      style={{
                        padding: '4px 10px',
                        borderRadius: '6px',
                        background: 'rgba(239, 68, 68, 0.2)',
                        border: 'none',
                        color: '#ef4444',
                        fontSize: 'clamp(10px, 2.8vw, 12px)',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      {cd.day}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Calendar Card */}
        <div style={{ background: STYLES.bg.card, borderRadius: 'clamp(10px, 3vw, 16px)', border: `1px solid ${STYLES.bg.border}`, padding: 'clamp(12px, 3vw, 20px)', marginBottom: '16px' }}>
          {/* Legend */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#22c55e' }} />
              <span style={{ fontSize: 'clamp(9px, 2.2vw, 10px)', color: STYLES.text.muted }}>Favorable</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#f59e0b' }} />
              <span style={{ fontSize: 'clamp(9px, 2.2vw, 10px)', color: STYLES.text.muted }}>Mixed</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#ef4444' }} />
              <span style={{ fontSize: 'clamp(9px, 2.2vw, 10px)', color: STYLES.text.muted }}>Challenging</span>
            </div>
          </div>
          
          {/* Day headers */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: '12px' }}>
            {days.map(d => (
              <div key={d} style={{ textAlign: 'center', fontSize: 'clamp(10px, 2.5vw, 11px)', color: STYLES.text.muted, fontWeight: '500', padding: '8px 0' }}>{d}</div>
            ))}
          </div>
          
          {/* Calendar grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
            {Array.from({ length: firstDay }).map((_, i) => <div key={`e-${i}`} />)}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const dayEvents = monthEvents[day] || [];
              const isToday = isCurrentMonth && day === today.getDate();
              const isSelected = selectedDay === day;
              const hasEvents = dayEvents.length > 0;
              
              // Get quality indicator from computed data
              const qualityEvent = dayEvents.find(e => e.type === 'day_quality');
              const dayQuality = qualityEvent?.level || 'neutral';
              const qualityScore = qualityEvent?.qualityScore;
              
              // Quality-based background colors
              const qualityBgColors = {
                'highly_favorable': 'rgba(34, 197, 94, 0.2)',   // Green
                'favorable': 'rgba(132, 204, 22, 0.15)',        // Lime
                'mixed': 'rgba(245, 158, 11, 0.12)',            // Amber
                'challenging': 'rgba(239, 68, 68, 0.15)',       // Red
                'difficult': 'rgba(220, 38, 38, 0.2)',          // Dark red
                'neutral': 'transparent'
              };
              
              // Quality dot colors
              const qualityDotColors = {
                'highly_favorable': '#22c55e',
                'favorable': '#84cc16',
                'mixed': '#f59e0b',
                'challenging': '#ef4444',
                'difficult': '#dc2626',
                'neutral': STYLES.accent
              };
              
              return (
                <button
                  key={day}
                  onClick={() => setSelectedDay(isSelected ? null : day)}
                  style={{
                    aspectRatio: '1',
                    borderRadius: '10px',
                    border: 'none',
                    background: isSelected 
                      ? 'rgba(116,179,255,0.15)' 
                      : isToday 
                        ? 'rgba(116,179,255,0.08)' 
                        : qualityBgColors[dayQuality] || 'transparent',
                    cursor: 'pointer',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s'
                  }}
                  title={qualityEvent ? `${dayQuality.replace('_', ' ')} (Score: ${qualityScore})` : ''}
                >
                  <span style={{ 
                    fontSize: STYLES.fontSize.sm, 
                    color: isToday ? STYLES.accent : STYLES.text.secondary, 
                    fontWeight: isToday ? '600' : '400' 
                  }}>{day}</span>
                  {hasEvents && (
                    <div style={{ display: 'flex', gap: '2px', marginTop: '4px' }}>
                      <div style={{ 
                        width: '6px', 
                        height: '6px', 
                        borderRadius: '50%', 
                        background: qualityDotColors[dayQuality] || STYLES.accent
                      }} />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Selected Day Events */}
        {selectedDay && (
          <div style={{ background: STYLES.bg.card, borderRadius: 'clamp(10px, 3vw, 16px)', border: `1px solid ${STYLES.bg.border}`, padding: 'clamp(12px, 3vw, 20px)', marginBottom: '16px' }}>
            {/* Day Header */}
            {(() => {
              const dayEvents = monthEvents[selectedDay] || [];
              const qualityEvent = dayEvents.find(e => e.type === 'day_quality');
              const dayQuality = qualityEvent?.level || 'neutral';
              const qualityScore = qualityEvent?.qualityScore || 0;
              
              const qualityColors = {
                'highly_favorable': '#22c55e',
                'favorable': '#84cc16',
                'mixed': '#f59e0b',
                'challenging': '#ef4444',
                'difficult': '#dc2626'
              };
              
              return (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', paddingBottom: '16px', borderBottom: `1px solid ${STYLES.bg.border}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ 
                        width: '48px', 
                        height: '48px', 
                        borderRadius: 'clamp(8px, 2vw, 12px)', 
                        background: qualityColors[dayQuality] ? `${qualityColors[dayQuality]}20` : 'rgba(116,179,255,0.1)', 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        border: `2px solid ${qualityColors[dayQuality] || STYLES.accent}`
                      }}>
                        <span style={{ fontSize: 'clamp(14px, 4vw, 18px)', fontWeight: '600', color: qualityColors[dayQuality] || STYLES.accent }}>{selectedDay}</span>
                        <span style={{ fontSize: 'clamp(9px, 2.2vw, 10px)', color: STYLES.text.muted }}>{days[new Date(selectedYear, selectedMonth, selectedDay).getDay()]}</span>
                      </div>
                      <div>
                        <p style={{ fontSize: STYLES.fontSize.lg, color: STYLES.text.primary, fontWeight: '500' }}>{months[selectedMonth]} {selectedDay}</p>
                        <p style={{ 
                          fontSize: STYLES.fontSize.sm, 
                          color: qualityColors[dayQuality] || STYLES.text.muted,
                          fontWeight: '600'
                        }}>
                          {dayQuality === 'highly_favorable' ? 'EXCELLENT DAY' : 
                           dayQuality === 'favorable' ? 'GOOD DAY' :
                           dayQuality === 'challenging' ? 'BE CAUTIOUS' :
                           dayQuality === 'difficult' ? 'REST DAY' : 
                           dayQuality.replace('_', ' ').toUpperCase()}
                        </p>
                      </div>
                    </div>
                    <button 
                      onClick={() => setSelectedDay(null)}
                      style={{ background: 'none', border: 'none', color: STYLES.text.muted, fontSize: 'clamp(14px, 4vw, 18px)', cursor: 'pointer', padding: '8px' }}
                    >
                      ✕
                    </button>
                  </div>
                  
                  {/* Dasha Info for this day */}
                  {qualityEvent && (
                    <div style={{ 
                      background: 'rgba(116,179,255,0.05)', 
                      borderRadius: '10px', 
                      padding: '12px', 
                      marginBottom: '16px',
                      border: '1px solid rgba(116,179,255,0.1)'
                    }}>
                      <p style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', color: STYLES.text.muted, marginBottom: '6px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Day Overview
                      </p>
                      <p style={{ fontSize: STYLES.fontSize.base, color: STYLES.text.secondary }}>
                        {dayQuality === 'highly_favorable' ? 'Excellent day for important activities and decisions' : 
                         dayQuality === 'favorable' ? 'Good day for productive activities' :
                         dayQuality === 'challenging' ? 'Take extra care with important matters today' :
                         dayQuality === 'difficult' ? 'Best to focus on rest and routine tasks' : 
                         'Balanced energy for everyday activities'}
                      </p>
                    </div>
                  )}
                </>
              );
            })()}
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {monthEvents[selectedDay]?.filter(e => e.type !== 'day_quality').length > 0 ? (
                monthEvents[selectedDay].filter(e => e.type !== 'day_quality').map((evt, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '12px', background: STYLES.bg.hover, borderRadius: '10px' }}>
                    <div style={{ width: '3px', minHeight: '36px', borderRadius: '2px', background: evt.color || STYLES.accent, flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', color: evt.color || STYLES.text.muted, fontWeight: '600', marginBottom: '4px' }}>{evt.name}</p>
                      <p style={{ fontSize: STYLES.fontSize.base, color: STYLES.text.secondary, lineHeight: '1.6' }}>{evt.event}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p style={{ fontSize: STYLES.fontSize.sm, color: STYLES.text.muted, textAlign: 'center', padding: 'clamp(12px, 3vw, 20px)' }}>No specific domain events for this day</p>
              )}
            </div>
          </div>
        )}

        {/* TODAY'S PREDICTIONS - Prominent section for current day */}
        {isCurrentMonth && (
          <div style={{ 
            background: 'linear-gradient(135deg, rgba(116,179,255,0.08) 0%, rgba(139,92,246,0.05) 100%)', 
            borderRadius: 'clamp(10px, 3vw, 16px)', 
            border: '1px solid rgba(116,179,255,0.2)', 
            padding: 'clamp(12px, 3vw, 20px)',
            marginBottom: '16px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <div>
                <p style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: 'clamp(11px, 3vw, 13px)',
                  color: '#F3E5AB',
                  fontWeight: 400,
                  textTransform: 'uppercase',
                  letterSpacing: '0.25em',
                  marginBottom: '4px',
                }}>
                  Today&apos;s Forecast
                </p>
                <p style={{ fontSize: 'clamp(14px, 4vw, 18px)', color: STYLES.text.primary, fontWeight: '500' }}>
                  {months[selectedMonth]} {today.getDate()}, {selectedYear}
                </p>
              </div>
              {(() => {
                const todayEvents = monthEvents[today.getDate()] || [];
                const todayQuality = todayEvents.find(e => e.type === 'day_quality');
                const quality = todayQuality?.level || 'mixed';
                const qualityColors = {
                  'highly_favorable': '#22c55e',
                  'favorable': '#84cc16',
                  'mixed': '#f59e0b',
                  'challenging': '#ef4444',
                  'difficult': '#dc2626'
                };
                const qualityLabels = {
                  'highly_favorable': 'EXCELLENT',
                  'favorable': 'GOOD',
                  'mixed': 'MIXED',
                  'challenging': 'CAUTION',
                  'difficult': 'REST'
                };
                return (
                  <div style={{ 
                    background: `${qualityColors[quality]}20`, 
                    padding: '8px 16px', 
                    borderRadius: '20px',
                    border: `1px solid ${qualityColors[quality]}40`
                  }}>
                    <span style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', fontWeight: '700', color: qualityColors[quality], letterSpacing: '0.1em' }}>
                      {qualityLabels[quality] || 'TODAY'}
                    </span>
                  </div>
                );
              })()}
            </div>
            
            {/* Today's domain predictions */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
              {(() => {
                const todayEvents = monthEvents[today.getDate()] || [];
                const domainEvents = todayEvents.filter(e => e.type !== 'day_quality' && e.event);
                
                if (domainEvents.length === 0) {
                  return (
                    <p style={{ gridColumn: '1 / -1', fontSize: 'clamp(12px, 3.2vw, 14px)', color: STYLES.text.muted, textAlign: 'center', padding: 'clamp(10px, 2.5vw, 16px)' }}>
                      Loading today&apos;s predictions...
                    </p>
                  );
                }
                
                // Group by domain and show first event per domain
                const domainMap = {};
                domainEvents.forEach(evt => {
                  if (!domainMap[evt.domain]) {
                    domainMap[evt.domain] = evt;
                  }
                });
                
                const domainOrder = ['career', 'wealth', 'health', 'marriage', 'family', 'spiritual', 'travel', 'education'];
                
                return domainOrder.map(domain => {
                  const evt = domainMap[domain];
                  if (!evt) return null;
                  
                  return (
                    <div key={domain} style={{ 
                      background: 'rgba(0,0,0,0.2)', 
                      borderRadius: '10px', 
                      padding: '12px',
                      borderLeft: `3px solid ${evt.color}`
                    }}>
                      <p style={{ fontSize: 'clamp(9px, 2.2vw, 10px)', color: evt.color, fontWeight: '600', textTransform: 'uppercase', marginBottom: '4px' }}>
                        {evt.name}
                      </p>
                      <p style={{ fontSize: 'clamp(11px, 3vw, 13px)', color: STYLES.text.secondary, lineHeight: '1.4' }}>
                        {evt.event}
                      </p>
                    </div>
                  );
                }).filter(Boolean);
              })()}
            </div>
          </div>
        )}

        {/* Month Events List - SHOWS WEEKLY BREAKDOWN OF KEY EVENTS */}
        <div style={{ background: STYLES.bg.card, borderRadius: 'clamp(10px, 3vw, 16px)', border: `1px solid ${STYLES.bg.border}`, padding: 'clamp(12px, 3vw, 20px)' }}>
          <p style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 'clamp(13px, 3.5vw, 16px)',
            color: '#F3E5AB',
            fontWeight: 400,
            textTransform: 'uppercase',
            letterSpacing: '0.2em',
            marginBottom: '16px',
          }}>
            {months[selectedMonth]} {selectedYear} Weekly Forecast
          </p>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {(() => {
              // Organize events by WEEK - prefer GPT weekly_predictions when available
              const weeks = [
                { label: 'Week 1', key: 'week_1', days: [1, 2, 3, 4, 5, 6, 7], range: '1-7' },
                { label: 'Week 2', key: 'week_2', days: [8, 9, 10, 11, 12, 13, 14], range: '8-14' },
                { label: 'Week 3', key: 'week_3', days: [15, 16, 17, 18, 19, 20, 21], range: '15-21' },
                { label: 'Week 4', key: 'week_4', days: [22, 23, 24, 25, 26, 27, 28, 29, 30, 31].filter(d => d <= daysInMonth), range: `22-${daysInMonth}` }
              ];
              
              const domainColors = {
                health: '#ef4444',
                career: '#3b82f6',
                wealth: '#22c55e',
                marriage: '#ec4899',
                education: '#8b5cf6',
                spiritual: '#f59e0b',
                travel: '#06b6d4',
                family: '#84cc16'
              };
              
              const domainOrder = ['career', 'wealth', 'health', 'marriage', 'family', 'travel'];
              
              // Check for GPT-powered weekly predictions
              const gptWeekly = detailedMonthlyData?.weekly_predictions;
              
              return weeks.map((week, weekIdx) => {
                // Use GPT weekly predictions if available
                const gptWeekData = gptWeekly?.[week.key];
                
                // Fallback to computed daily events if no GPT data
                let domainTopEvents = {};
                if (gptWeekData && typeof gptWeekData === 'object') {
                  // Use GPT-powered predictions
                  domainOrder.forEach(domain => {
                    const pred = gptWeekData[domain];
                    if (pred) {
                      const text = typeof pred === 'string' ? pred : pred.text || pred.event || '';
                      const day = typeof pred === 'object' ? (pred.day || week.days[0]) : week.days[0];
                      if (text) {
                        domainTopEvents[domain] = [{ event: text, days: [day], domain }];
                      }
                    }
                  });
                } else {
                  // Fallback: aggregate from daily computed events
                  const weekEvents = {};
                  week.days.forEach(day => {
                    const dayEvents = monthEvents[day] || [];
                    dayEvents.filter(e => e.type !== 'day_quality' && e.event).forEach(evt => {
                      const key = `${evt.domain}:${evt.event}`;
                      if (!weekEvents[key]) {
                        weekEvents[key] = { ...evt, days: [day] };
                      } else if (!weekEvents[key].days.includes(day)) {
                        weekEvents[key].days.push(day);
                      }
                    });
                  });
                  
                  const weekEventsList = Object.values(weekEvents);
                  domainOrder.forEach(domain => {
                    const domainEvts = weekEventsList.filter(e => e.domain === domain);
                    domainEvts.sort((a, b) => a.days.length - b.days.length);
                    domainTopEvents[domain] = domainEvts.slice(0, 2);
                  });
                }
                
                const hasEvents = Object.values(domainTopEvents).some(arr => arr.length > 0);
                if (!hasEvents) return null;
                
                return (
                  <div key={weekIdx} style={{ 
                    background: 'rgba(255,255,255,0.02)', 
                    borderRadius: 'clamp(8px, 2vw, 12px)', 
                    padding: 'clamp(10px, 2.5vw, 16px)',
                    border: '1px solid rgba(255,255,255,0.05)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                      <span style={{ fontSize: 'clamp(11px, 3vw, 13px)', fontWeight: '600', color: STYLES.text.primary }}>
                        {week.label}
                      </span>
                      <span style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', color: STYLES.text.muted }}>
                        ({months[selectedMonth].slice(0, 3)} {week.range})
                      </span>
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                      {domainOrder.slice(0, 6).map(domain => {
                        const events = domainTopEvents[domain];
                        if (!events || events.length === 0) return null;
                        
                        const evt = events[0];
                        const dayText = evt.days.length === 1 
                          ? `Day ${evt.days[0]}` 
                          : evt.days.length <= 3 
                            ? `Days ${evt.days.join(', ')}` 
                            : `Days ${evt.days[0]}-${evt.days[evt.days.length - 1]}`;
                        
                        return (
                          <div key={domain} style={{ 
                            padding: '10px', 
                            background: 'rgba(0,0,0,0.15)', 
                            borderRadius: '8px',
                            borderLeft: `2px solid ${domainColors[domain]}`
                          }}>
                            <p style={{ fontSize: 'clamp(8px, 2vw, 9px)', color: domainColors[domain], fontWeight: '600', textTransform: 'uppercase', marginBottom: '2px' }}>
                              {domain}
                            </p>
                            <p style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', color: STYLES.text.secondary, lineHeight: '1.3', marginBottom: '3px' }}>
                              {evt.event}
                            </p>
                            <p style={{ fontSize: 'clamp(8px, 2vw, 9px)', color: STYLES.text.muted }}>
                              {dayText}
                            </p>
                          </div>
                        );
                      }).filter(Boolean)}
                    </div>
                  </div>
                );
              }).filter(Boolean);
            })()}
          </div>
        </div>
        
        {/* Monthly Overview - GPT Deep Dive Analysis (below calendar + weekly) */}
        {detailedMonthlyData?.monthly_overview && typeof detailedMonthlyData.monthly_overview === 'string' && detailedMonthlyData.monthly_overview.length > 50 && (
          <div style={{ background: STYLES.bg.card, borderRadius: 'clamp(10px, 3vw, 16px)', border: `1px solid ${STYLES.bg.border}`, padding: 'clamp(12px, 3vw, 20px)', marginTop: '16px' }}>
            <p style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 'clamp(13px, 3.5vw, 16px)',
              color: '#F3E5AB',
              fontWeight: 400,
              textTransform: 'uppercase',
              letterSpacing: '0.22em',
              marginBottom: '16px',
            }}>
              Monthly Overview
            </p>
            {(() => {
              let rawText = detailedMonthlyData.monthly_overview;
              rawText = rawText.replace(/\*\*/g, '').replace(/\*/g, '');
              const headerPattern = /\s+(MONTH THEME|HEALTH|CAREER|WEALTH|EDUCATION|FAMILY|RELATIONSHIPS|MARRIAGE|TRAVEL|SPIRITUALITY|BEST DAYS|CAUTION DAYS)\s/g;
              rawText = rawText.replace(headerPattern, '\n\n$1 ');
              rawText = rawText.replace(/^(MONTH THEME|HEALTH|CAREER|WEALTH|EDUCATION|FAMILY|RELATIONSHIPS|MARRIAGE|TRAVEL|SPIRITUALITY|BEST DAYS|CAUTION DAYS)\s/gm, '\n\n$1 ');
              rawText = rawText.replace(/\n{3,}/g, '\n\n').trim();
              
              const domainColors = {
                'HEALTH': '#22c55e', 'CAREER': '#3b82f6', 'WEALTH': '#f59e0b',
                'EDUCATION': '#8b5cf6', 'FAMILY': '#ec4899', 'RELATIONSHIPS': '#f43f5e', 'MARRIAGE': '#f43f5e',
                'TRAVEL': '#06b6d4', 'SPIRITUALITY': '#a855f7', 'MONTH THEME': '#74b3ff',
                'BEST DAYS': '#22c55e', 'CAUTION DAYS': '#ef4444'
              };
              const domainHeadersList = Object.keys(domainColors);
              
              return rawText.split('\n\n').map((para, idx) => {
                const cleaned = para.trim();
                if (!cleaned) return null;
                const upperCleaned = cleaned.toUpperCase();
                const matchedHeader = domainHeadersList.find(h => upperCleaned.startsWith(h));
                
                if (matchedHeader) {
                  const content = cleaned.substring(matchedHeader.length).trim();
                  return (
                    <div key={idx} style={{ marginBottom: '16px', paddingBottom: '12px', borderBottom: `1px solid ${STYLES.bg.border}` }}>
                      <p style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', color: domainColors[matchedHeader] || STYLES.accent, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>
                        {matchedHeader}
                      </p>
                      <p style={{ fontSize: STYLES.fontSize.base, color: STYLES.text.secondary, lineHeight: '1.8' }}>
                        {content}
                      </p>
                    </div>
                  );
                }
                return (
                  <p key={idx} style={{ fontSize: STYLES.fontSize.base, color: STYLES.text.secondary, lineHeight: '1.8', marginBottom: '12px' }}>
                    {cleaned}
                  </p>
                );
              });
            })()}
          </div>
        )}
      </div>
    );
  };

  const renderYearlyView = () => {
    // If we have the detailed LLM report, show it
    if (detailedYearlyReport?.full_report) {
      return (
        <div style={{ paddingBottom: '96px' }}>
          {/* Clean Header - matching Insights styling */}
          <div style={{ textAlign: 'center', padding: '24px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)', margin: '0 16px 24px' }}>
            <p style={{ fontSize: 'clamp(9px, 2.2vw, 10px)', color: 'rgba(116,179,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: '8px', fontWeight: '500' }}>Taraanaya</p>
            <h1 style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 'clamp(18px, 5vw, 22px)',
              color: '#F3E5AB',
              margin: 0,
              fontWeight: 300,
              letterSpacing: '0.3em',
              textTransform: 'uppercase',
            }}>Yearly Prediction</h1>
            <p style={{ fontSize: 'clamp(12px, 3.2vw, 14px)', color: 'rgba(255,255,255,0.6)', marginTop: '4px', fontWeight: '400' }}>{selectedYear}</p>
            
            {/* Native Info - Clean Grid */}
            <div style={{ marginTop: '24px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', maxWidth: '280px', marginLeft: 'auto', marginRight: 'auto', textAlign: 'left' }}>
              <div>
                <p style={{ fontSize: 'clamp(9px, 2.2vw, 10px)', color: 'rgba(116,179,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px', fontWeight: '500' }}>Lagna</p>
                <p style={{ fontSize: 'clamp(11px, 3vw, 13px)', color: 'rgba(255,255,255,0.9)', fontWeight: '500' }}>{detailedYearlyReport.native?.lagna?.split(' ')[0]}</p>
              </div>
              <div>
                <p style={{ fontSize: 'clamp(9px, 2.2vw, 10px)', color: 'rgba(116,179,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px', fontWeight: '500' }}>Moon</p>
                <p style={{ fontSize: 'clamp(11px, 3vw, 13px)', color: 'rgba(255,255,255,0.9)', fontWeight: '500' }}>{detailedYearlyReport.native?.moon?.split(' ')[0]}</p>
              </div>
              <div>
                <p style={{ fontSize: 'clamp(9px, 2.2vw, 10px)', color: 'rgba(116,179,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px', fontWeight: '500' }}>Mahadasha</p>
                <p style={{ fontSize: 'clamp(11px, 3vw, 13px)', color: 'rgba(255,255,255,0.9)', fontWeight: '500' }}>{detailedYearlyReport.dasha?.mahadasha}</p>
              </div>
              <div>
                <p style={{ fontSize: 'clamp(9px, 2.2vw, 10px)', color: 'rgba(116,179,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px', fontWeight: '500' }}>Antardasha</p>
                <p style={{ fontSize: 'clamp(11px, 3vw, 13px)', color: 'rgba(255,255,255,0.9)', fontWeight: '500' }}>{detailedYearlyReport.dasha?.antardasha}</p>
              </div>
            </div>
            <p style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', color: 'rgba(255,255,255,0.35)', marginTop: '12px', fontWeight: '400' }}>
              {detailedYearlyReport.dasha?.antardasha_dates}
            </p>
          </div>

          {/* Report Content - matching Insights font styling */}
          <div style={{ padding: '0 16px' }}>
            {(() => {
              // Pre-process: ensure domain headers are separated by \n\n
              let rawReport = detailedYearlyReport.full_report || '';
              // Strip markdown
              rawReport = rawReport.replace(/\*\*/g, '').replace(/\*/g, '');
              // Insert \n\n before known domain headers if not already separated
              const domainHeaderPattern = /\s+(YEAR THEME|HEALTH|CAREER|WEALTH|EDUCATION|FAMILY|RELATIONSHIPS|MARRIAGE|TRAVEL|SPIRITUALITY)\s/g;
              rawReport = rawReport.replace(domainHeaderPattern, '\n\n$1 ');
              // Also handle headers at the very start
              rawReport = rawReport.replace(/^(YEAR THEME|HEALTH|CAREER|WEALTH|EDUCATION|FAMILY|RELATIONSHIPS|MARRIAGE|TRAVEL|SPIRITUALITY)\s/gm, '\n\n$1 ');
              // Clean triple+ newlines
              rawReport = rawReport.replace(/\n{3,}/g, '\n\n').trim();
              
              // Pre-merge: if a domain header is on its own line, merge with next paragraph
              const domainHeaders = ['HEALTH', 'CAREER', 'WEALTH', 'EDUCATION', 'FAMILY', 'RELATIONSHIPS', 'MARRIAGE', 'TRAVEL', 'SPIRITUALITY'];
              const paragraphs = rawReport.split('\n\n');
              const merged = [];
              for (let i = 0; i < paragraphs.length; i++) {
                const p = paragraphs[i].trim();
                if (!p) continue;
                const upper = p.toUpperCase().trim();
                // If this paragraph is JUST a domain header, merge with next
                if (domainHeaders.includes(upper) || domainHeaders.some(d => upper === d + ':')) {
                  const next = (paragraphs[i + 1] || '').trim();
                  merged.push(p + '\n' + next);
                  i++; // skip next since we merged
                } else {
                  merged.push(p);
                }
              }
              
              return merged.map((rawParagraph, idx) => {
              const paragraph = rawParagraph.trim();
              if (!paragraph) return null;
              
              // YEAR THEME section (new format)
              if (paragraph.toUpperCase().startsWith('YEAR THEME')) {
                const content = paragraph.replace(/^YEAR THEME\s*/i, '').trim();
                return (
                  <div key={idx} style={{ marginBottom: '32px', paddingBottom: '24px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    <p style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', color: 'rgba(116,179,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '14px', fontWeight: '500' }}>Year Theme</p>
                    <p style={{ fontSize: 'clamp(13px, 3.5vw, 15px)', lineHeight: '1.9', color: 'rgba(255,255,255,0.75)', fontWeight: '400' }}>
                      {content}
                    </p>
                  </div>
                );
              }
              
              // Legacy THEME section
              if (paragraph.startsWith('THEME OF')) {
                return (
                  <div key={idx} style={{ marginBottom: '32px', paddingBottom: '24px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    <p style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', color: 'rgba(116,179,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '14px', fontWeight: '500' }}>Theme of {selectedYear}</p>
                    <p style={{ fontSize: 'clamp(13px, 3.5vw, 15px)', lineHeight: '1.9', color: 'rgba(255,255,255,0.75)', fontWeight: '400' }}>
                      {paragraph.replace(/^THEME OF \d+ \(ONE-LINE TRUTH\)\s*/, '')}
                    </p>
                  </div>
                );
              }
              
              // DOMAIN headers (HEALTH, CAREER, WEALTH, etc.) - new clean format
              const domainHeaders = ['HEALTH', 'CAREER', 'WEALTH', 'EDUCATION', 'FAMILY', 'RELATIONSHIPS', 'TRAVEL', 'SPIRITUALITY'];
              const upperPara = paragraph.trim().toUpperCase();
              const matchedDomain = domainHeaders.find(d => upperPara.startsWith(d));
              
              if (matchedDomain) {
                const headerLine = paragraph.split('\n')[0];
                const content = paragraph.substring(headerLine.length).trim();
                
                return (
                  <div key={idx} style={{ marginTop: '28px', marginBottom: '16px' }}>
                    <p style={{ fontSize: 'clamp(11px, 3vw, 13px)', color: 'rgba(116,179,255,0.7)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '14px', paddingBottom: '10px', borderBottom: '1px solid rgba(116,179,255,0.15)', fontWeight: '600' }}>
                      {matchedDomain.charAt(0) + matchedDomain.slice(1).toLowerCase()}
                    </p>
                    {content && (
                      <p style={{ fontSize: 'clamp(13px, 3.5vw, 15px)', lineHeight: '1.9', color: 'rgba(255,255,255,0.7)', fontWeight: '400' }}>
                        {content}
                      </p>
                    )}
                  </div>
                );
              }
              
              // Legacy DOMAIN headers
              if (paragraph.startsWith('DOMAIN')) {
                const domainMatch = paragraph.match(/DOMAIN \d+ — (.+) \(\d+\)/);
                const domainName = domainMatch ? domainMatch[1] : paragraph;
                return (
                  <div key={idx} style={{ marginTop: '28px', marginBottom: '16px' }}>
                    <p style={{ fontSize: 'clamp(11px, 3vw, 13px)', color: 'rgba(116,179,255,0.7)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '14px', paddingBottom: '10px', borderBottom: '1px solid rgba(116,179,255,0.15)', fontWeight: '600' }}>
                      {domainName}
                    </p>
                  </div>
                );
              }
              
              // Sub-headers (Year Pattern:, Body Focus:, etc.)
              if (paragraph.match(/^(Year Pattern|Body Focus|Longevity|Career Reality|What \d+ Is|What \d+ IS|Timeline|Money Flow|Business|Property|Favored|Best periods|Family|Parents|Children|Relationship Tone|Truth|Travel|Foreign)/) || 
                  (paragraph.endsWith(':') && paragraph.length < 50)) {
                return (
                  <p key={idx} style={{ fontSize: 'clamp(11px, 3vw, 13px)', color: 'rgba(255,255,255,0.55)', fontWeight: '500', marginTop: '18px', marginBottom: '10px' }}>
                    {paragraph.replace(/:$/, '')}
                  </p>
                );
              }
              
              // Verdict lines - subtle styling
              if (paragraph.toLowerCase().includes('verdict')) {
                return (
                  <p key={idx} style={{ fontSize: 'clamp(11px, 3vw, 13px)', color: 'rgba(255,255,255,0.45)', fontStyle: 'italic', marginTop: '18px', marginBottom: '26px', paddingLeft: '14px', borderLeft: '2px solid rgba(116,179,255,0.25)' }}>
                    {paragraph}
                  </p>
                );
              }
              
              // Bullet points
              if (paragraph.startsWith('-') || paragraph.startsWith('•')) {
                const lines = paragraph.split('\n').filter(line => line.trim());
                return (
                  <div key={idx} style={{ margin: '14px 0' }}>
                    {lines.map((line, lineIdx) => (
                      <div key={lineIdx} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '10px' }}>
                        <span style={{ color: 'rgba(116,179,255,0.4)', marginTop: '8px', fontSize: '6px' }}>●</span>
                        <span style={{ fontSize: 'clamp(13px, 3.5vw, 15px)', lineHeight: '1.8', color: 'rgba(255,255,255,0.7)', fontWeight: '400' }}>{line.replace(/^[-•]\s*/, '')}</span>
                      </div>
                    ))}
                  </div>
                );
              }
              
              // Regular paragraph - main body text
              if (paragraph.trim()) {
                return (
                  <p key={idx} style={{ fontSize: 'clamp(13px, 3.5vw, 15px)', lineHeight: '1.9', color: 'rgba(255,255,255,0.7)', fontWeight: '400', margin: '14px 0' }}>
                    {paragraph}
                  </p>
                );
              }
              
              return null;
            });
            })()}
          </div>

          {/* Footer */}
          <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid rgba(255,255,255,0.05)', margin: '32px 16px 0', textAlign: 'center' }}>
            <p style={{ fontSize: 'clamp(9px, 2.2vw, 10px)', color: 'rgba(255,255,255,0.25)', fontWeight: '400' }}>
              Generated using Vedic astrology calculations
            </p>
            <p style={{ fontSize: 'clamp(9px, 2.2vw, 10px)', color: 'rgba(255,255,255,0.2)', marginTop: '4px', fontWeight: '400' }}>
              Next Mahadasha: {detailedYearlyReport.dasha?.next_mahadasha} (from {detailedYearlyReport.dasha?.next_mahadasha_start})
            </p>
          </div>
        </div>
      );
    }

    // Loading state for detailed report
    if (loadingYearlyReport) {
      return (
        <div className="flex flex-col items-center justify-center h-64">
          <div className="w-5 h-5 border border-white/20 border-t-white/50 rounded-full animate-spin" />
          <p className="text-white/40 text-xs mt-4">Generating predictions...</p>
        </div>
      );
    }

    // If no detailed report, check if it's because of no birth data
    if (!detailedYearlyReport?.full_report) {
      if (detailedYearlyReport?.full_report === 'No birth data available. Please complete your birth details first.') {
        return (
          <div className="flex flex-col items-center justify-center h-64 px-8 text-center">
            <p className="text-white/60 text-sm mb-2">No Birth Data Found</p>
            <p className="text-white/40 text-xs mb-4">Please complete your birth details to see yearly predictions.</p>
            <button 
              onClick={() => window.location.href = '/'}
              className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white/70 text-xs transition"
            >
              Enter Birth Details
            </button>
          </div>
        );
      }
      
      // Otherwise still loading
      return (
        <div className="flex flex-col items-center justify-center h-64">
          <div className="w-5 h-5 border border-white/20 border-t-white/50 rounded-full animate-spin" />
          <p className="text-white/40 text-xs mt-4">Loading yearly predictions...</p>
          <p className="text-white/30 text-[10px] mt-2">Please wait while we compute your timeline</p>
        </div>
      );
    }
    
    // OLD FALLBACK REMOVED - We now always wait for the detailed report
    // const yearlyData = getYearlyOverview();
    
    // This code should never be reached now, but kept as safety
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <p className="text-white/40 text-xs">No yearly data available</p>
        <button 
          onClick={refreshPredictions}
          className="mt-4 px-4 py-2 bg-white/10 rounded-lg text-white/60 text-xs"
        >
          Refresh
        </button>
      </div>
    );
  };

  // NEW: Render God-Level Events View
  const renderGodLevelEventsView = () => {
    // Severity config for styling
    const severityConfig = {
      high: { bg: 'rgba(239,68,68,0.15)', border: 'rgba(239,68,68,0.3)', text: '#f87171' },
      caution: { bg: 'rgba(251,191,36,0.15)', border: 'rgba(251,191,36,0.3)', text: '#fbbf24' },
      medium: { bg: 'rgba(59,130,246,0.15)', border: 'rgba(59,130,246,0.3)', text: '#60a5fa' },
      positive: { bg: 'rgba(34,197,94,0.15)', border: 'rgba(34,197,94,0.3)', text: '#4ade80' },
      low: { bg: 'rgba(107,114,128,0.15)', border: 'rgba(107,114,128,0.3)', text: '#9ca3af' },
    };
    
    const domainEmojis = { health: '❤️', career: '💼', wealth: '💰', marriage: '💍', travel: '✈️', spiritual: '🕉️', family: '👨‍👩‍👧', education: '📚' };
    
    const navigatePrevWeek = () => {
      const newDate = new Date(godLevelWeekStart);
      newDate.setDate(newDate.getDate() - 7);
      setGodLevelWeekStart(newDate);
      setGodLevelData(null);
    };
    
    const navigateNextWeek = () => {
      const newDate = new Date(godLevelWeekStart);
      newDate.setDate(newDate.getDate() + 7);
      setGodLevelWeekStart(newDate);
      setGodLevelData(null);
    };
    
    if (godLevelLoading) {
      return (
        <div className="flex flex-col items-center justify-center h-64">
          <div className="w-5 h-5 border border-amber-400/30 border-t-amber-400 rounded-full animate-spin" />
          <p className="text-white/40 text-xs mt-4">Loading God-Level predictions...</p>
        </div>
      );
    }
    
    const predictions = godLevelData?.daily_predictions || {};
    const sortedDays = Object.entries(predictions).sort(([a], [b]) => new Date(a) - new Date(b));
    
    return (
      <div className="space-y-4">
        {/* Dasha Info */}
        {godLevelDasha && (
          <div style={{ background: 'linear-gradient(135deg, rgba(139,92,246,0.1), rgba(59,130,246,0.1))', borderRadius: 'clamp(8px, 2vw, 12px)', padding: '12px', border: '1px solid rgba(139,92,246,0.2)' }}>
            <p style={{ fontSize: 'clamp(9px, 2.2vw, 10px)', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', marginBottom: '4px' }}>Current 5-Level Dasha</p>
            <p style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', color: 'rgba(255,255,255,0.8)', fontFamily: 'monospace' }}>
              {godLevelDasha.mahadasha}-{godLevelDasha.antardasha}-{godLevelDasha.pratyantardasha}-{godLevelDasha.sookshma}-{godLevelDasha.prana}
            </p>
          </div>
        )}
        
        {/* Week Navigation */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 4px' }}>
          <button onClick={navigatePrevWeek} style={{ padding: '8px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', border: 'none', cursor: 'pointer' }}>
            <ChevronLeft className="w-4 h-4 text-white/60" />
          </button>
          <span style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', color: 'rgba(255,255,255,0.6)' }}>
            {godLevelData?.start_date} — {godLevelData?.end_date}
          </span>
          <button onClick={navigateNextWeek} style={{ padding: '8px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', border: 'none', cursor: 'pointer' }}>
            <ChevronRight className="w-4 h-4 text-white/60" />
          </button>
        </div>
        
        {/* Summary */}
        {godLevelData && (
          <div style={{ background: 'rgba(18,24,36,0.6)', borderRadius: 'clamp(8px, 2vw, 12px)', padding: '12px', border: '1px solid rgba(116,179,255,0.08)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', color: 'rgba(255,255,255,0.5)' }}>Days with events</span>
              <span style={{ fontSize: 'clamp(12px, 3.2vw, 14px)', color: 'rgba(255,255,255,0.9)', fontWeight: '600' }}>{godLevelData.days_with_events || 0} / 7</span>
            </div>
          </div>
        )}
        
        {/* Events List */}
        {sortedDays.length === 0 ? (
          <div style={{ background: 'rgba(18,24,36,0.6)', borderRadius: 'clamp(8px, 2vw, 12px)', padding: '32px', textAlign: 'center' }}>
            <p style={{ fontSize: 'clamp(11px, 3vw, 13px)', color: 'rgba(255,255,255,0.4)' }}>No significant events predicted this week.</p>
            <p style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', color: 'rgba(255,255,255,0.3)', marginTop: '4px' }}>This is actually a peaceful time!</p>
          </div>
        ) : (
          sortedDays.map(([dateStr, events]) => {
            const dayDate = new Date(dateStr);
            const dayName = dayDate.toLocaleDateString('en-US', { weekday: 'short' });
            const dayNum = dayDate.getDate();
            const monthName = dayDate.toLocaleDateString('en-US', { month: 'short' });
            const hasHighPriority = events.some(e => e.severity === 'high' || e.severity === 'caution');
            
            return (
              <div 
                key={dateStr}
                style={{
                  background: 'rgba(18,24,36,0.6)',
                  borderRadius: 'clamp(8px, 2vw, 12px)',
                  padding: 'clamp(10px, 2.5vw, 16px)',
                  border: hasHighPriority ? '1px solid rgba(239,68,68,0.3)' : '1px solid rgba(116,179,255,0.08)'
                }}
              >
                {/* Date Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(255,255,255,0.05)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: 'clamp(8px, 2vw, 9px)', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase' }}>{dayName}</span>
                    <span style={{ fontSize: 'clamp(12px, 3.2vw, 14px)', color: 'rgba(255,255,255,0.9)', fontWeight: '600' }}>{dayNum}</span>
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', color: 'rgba(255,255,255,0.7)' }}>{monthName} {dayNum}</p>
                    <p style={{ fontSize: 'clamp(9px, 2.2vw, 10px)', color: 'rgba(255,255,255,0.4)' }}>{events.length} event{events.length !== 1 ? 's' : ''}</p>
                  </div>
                  {hasHighPriority && (
                    <span style={{ fontSize: 'clamp(9px, 2.2vw, 10px)', color: '#f87171', background: 'rgba(239,68,68,0.1)', padding: '4px 8px', borderRadius: '6px' }}>Important</span>
                  )}
                </div>
                
                {/* Events */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {events.map((event, idx) => {
                    const severity = severityConfig[event.severity] || severityConfig.medium;
                    return (
                      <div 
                        key={idx}
                        style={{
                          background: severity.bg,
                          borderRadius: '10px',
                          padding: '10px 12px',
                          border: `1px solid ${severity.border}`
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                          <span style={{ fontSize: 'clamp(13px, 3.5vw, 16px)' }}>{domainEmojis[event.domain] || '📌'}</span>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                              <span style={{ fontSize: 'clamp(9px, 2.2vw, 10px)', color: 'rgba(255,255,255,0.5)', textTransform: 'capitalize' }}>{event.domain}</span>
                              <span style={{ fontSize: 'clamp(9px, 2.2vw, 10px)', color: severity.text, textTransform: 'capitalize' }}>{event.severity}</span>
                            </div>
                            <p style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', color: 'rgba(255,255,255,0.85)' }}>{event.event}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </div>
    );
  };

  return (
    <div className={`${localStorage.getItem('demo_source') === 'emergent' ? 'min-h-screen' : 'h-screen'} bg-gradient-to-b from-[#0a1628] via-[#0d1a30] to-[#0a1628] flex flex-col ${localStorage.getItem('demo_source') === 'emergent' ? '' : 'overflow-hidden'}`}>
      {/* Cosmic Star Background - Scattered stars, no repeating pattern */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0" style={{ opacity: 0.45 }}>
        <div style={{ position: 'absolute', width: '2px', height: '2px', borderRadius: '50%', background: 'white', left: '5%', top: '8%', boxShadow: '0 0 4px white' }} />
        <div style={{ position: 'absolute', width: '1px', height: '1px', borderRadius: '50%', background: 'white', left: '18%', top: '3%', boxShadow: '0 0 2px white' }} />
        <div style={{ position: 'absolute', width: '1.5px', height: '1.5px', borderRadius: '50%', background: 'white', left: '35%', top: '15%', boxShadow: '0 0 3px white' }} />
        <div style={{ position: 'absolute', width: '1px', height: '1px', borderRadius: '50%', background: 'white', left: '55%', top: '6%', boxShadow: '0 0 2px white' }} />
        <div style={{ position: 'absolute', width: '2px', height: '2px', borderRadius: '50%', background: 'white', left: '75%', top: '18%', boxShadow: '0 0 4px white' }} />
        <div style={{ position: 'absolute', width: '1px', height: '1px', borderRadius: '50%', background: 'white', left: '92%', top: '10%', boxShadow: '0 0 2px white' }} />
        <div style={{ position: 'absolute', width: '1.5px', height: '1.5px', borderRadius: '50%', background: 'white', left: '12%', top: '32%', boxShadow: '0 0 3px white' }} />
        <div style={{ position: 'absolute', width: '1px', height: '1px', borderRadius: '50%', background: 'white', left: '28%', top: '45%', boxShadow: '0 0 2px white' }} />
        <div style={{ position: 'absolute', width: '2px', height: '2px', borderRadius: '50%', background: 'white', left: '48%', top: '38%', boxShadow: '0 0 4px white' }} />
        <div style={{ position: 'absolute', width: '1px', height: '1px', borderRadius: '50%', background: 'white', left: '68%', top: '52%', boxShadow: '0 0 2px white' }} />
        <div style={{ position: 'absolute', width: '1.5px', height: '1.5px', borderRadius: '50%', background: 'white', left: '85%', top: '42%', boxShadow: '0 0 3px white' }} />
        <div style={{ position: 'absolute', width: '1px', height: '1px', borderRadius: '50%', background: 'white', left: '8%', top: '65%', boxShadow: '0 0 2px white' }} />
        <div style={{ position: 'absolute', width: '2px', height: '2px', borderRadius: '50%', background: 'white', left: '32%', top: '72%', boxShadow: '0 0 4px white' }} />
        <div style={{ position: 'absolute', width: '1px', height: '1px', borderRadius: '50%', background: 'white', left: '58%', top: '68%', boxShadow: '0 0 2px white' }} />
        <div style={{ position: 'absolute', width: '1.5px', height: '1.5px', borderRadius: '50%', background: 'white', left: '78%', top: '78%', boxShadow: '0 0 3px white' }} />
        <div style={{ position: 'absolute', width: '1px', height: '1px', borderRadius: '50%', background: 'white', left: '95%', top: '85%', boxShadow: '0 0 2px white' }} />
        <div style={{ position: 'absolute', width: '2px', height: '2px', borderRadius: '50%', background: 'white', left: '22%', top: '88%', boxShadow: '0 0 4px white' }} />
        <div style={{ position: 'absolute', width: '1px', height: '1px', borderRadius: '50%', background: 'white', left: '45%', top: '92%', boxShadow: '0 0 2px white' }} />
        {/* Subtle cosmic glow */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[400px] h-[400px] bg-amber-500/5 rounded-full blur-[120px]" />
      </div>
      
      {/* Header */}
      <div className="relative z-10 px-5 pb-3" style={{ paddingTop: 'calc(16px + env(safe-area-inset-top, 0px))', maxWidth: '900px', marginLeft: 'auto', marginRight: 'auto' }}>
        <h1 style={{
          textAlign: 'center',
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: 'clamp(18px, 5vw, 24px)',
          fontWeight: 300,
          color: '#F3E5AB',
          letterSpacing: '0.35em',
          textTransform: 'uppercase',
          marginBottom: '4px',
        }}>Timeline</h1>
        <p style={{
          textAlign: 'center',
          fontSize: 'clamp(10px, 2.5vw, 11px)',
          color: 'rgba(243,229,171,0.55)',
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          fontWeight: 500,
        }}>Your Monthly Outlook</p>
      </div>

      {/* Navigation */}
      <div className="px-5 py-3 flex items-center justify-center">
        <div className="text-center">
          <div className="flex items-center gap-2 justify-center">
            <p style={{ fontSize: 'clamp(13px, 3.5vw, 16px)', fontWeight: '500', color: 'rgba(255,255,255,0.9)' }}>
              {view === 'monthly' ? `${months[selectedMonth]} ${selectedYear}` : selectedYear}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '4px', marginTop: '10px', background: 'rgba(116,179,255,0.06)', borderRadius: '10px', padding: '3px' }}>
            <button 
              onClick={() => { setGodLevelMode(false); setView('monthly'); }} 
              style={{ 
                padding: '8px 14px', 
                borderRadius: '8px', 
                fontSize: 'clamp(10px, 2.5vw, 11px)', 
                fontWeight: '500', 
                border: 'none',
                cursor: 'pointer',
                background: view === 'monthly' ? 'rgba(116,179,255,0.15)' : 'transparent',
                color: view === 'monthly' ? '#74B3FF' : 'rgba(255,255,255,0.4)'
              }}
            >
              Month
            </button>
            <button 
              onClick={() => { setGodLevelMode(false); setView('yearly'); }} 
              style={{ 
                padding: '8px 14px', 
                borderRadius: '8px', 
                fontSize: 'clamp(10px, 2.5vw, 11px)', 
                fontWeight: '500', 
                border: 'none',
                cursor: 'pointer',
                background: view === 'yearly' ? 'rgba(116,179,255,0.15)' : 'transparent',
                color: view === 'yearly' ? '#74B3FF' : 'rgba(255,255,255,0.4)'
              }}
            >
              Year
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 px-4 overflow-y-auto pb-24 hide-scrollbar" style={{ WebkitOverflowScrolling: 'touch', maxWidth: '900px', marginLeft: 'auto', marginRight: 'auto' }}>
        {loading && !sessionLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '160px' }}>
            <div className="animate-spin" style={{ width: '24px', height: '24px', border: '2px solid rgba(116,179,255,0.2)', borderTopColor: STYLES.accent, borderRadius: '50%' }} />
            <p style={{ fontSize: STYLES.fontSize.sm, color: STYLES.text.muted, marginTop: '12px' }}>Loading predictions...</p>
          </div>
        ) : (!dataLoaded && !dashaHierarchy && Object.keys(allDomainsData).length === 0) ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '160px', textAlign: 'center' }}>
            <p style={{ fontSize: STYLES.fontSize.base, color: STYLES.text.muted }}>No timeline data</p>
            <p style={{ fontSize: STYLES.fontSize.sm, color: STYLES.text.muted, marginTop: '4px', opacity: 0.6 }}>Complete onboarding to see predictions</p>
          </div>
        ) : (
          view === 'monthly' ? renderMonthlyView() : renderYearlyView()
        )}
      </div>
    </div>
  );
};

export default EventTimeline;
// Cache bust: 1770355229
